/**
 * ADMIN.JS — LOGIMES Platform
 * Style identique à home.js : Outfit font · #6c63ff · même structure
 * Tabs: Vue d'ensemble · Factures · Clients · Archive · Tracabilité · Avoirs · Sandbox · Users · Sociétés · Paramètres
 * Nouvelles fonctionnalités (use case diagramme):
 *   1. Superviser les soumissions DSS (tab Factures + Vue d'ensemble)
 *   2. Tester sandbox ANCE (tab Sandbox — submit + résultats)
 *   3. Modifier infos sensibles société (modal 2 étapes: demande → validation)
 *   4. Valider inscription société + Gérer sociétés CRUD (tab Sociétés)
 * Auteur: PFE 2026 — ESEN Manouba
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const BACKEND_URL    = process.env.REACT_APP_BACKEND_URL;
const MIDDLEWARE_URL = process.env.REACT_APP_API_URL;


const getCookie = (name) => {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
};

// ─── WORKFLOW STEPS (identique à home.js) ────────────────────
const WORKFLOW_STEPS = [
  { id: "erp",      icon: "🏭", label: "DAS ERP",      color: "#6c63ff", statuts: ["C"] },
  { id: "xml",      icon: "📄", label: "XML TEIF",      color: "#0EA5E9", statuts: ["V"] },
  { id: "validate", icon: "✅", label: "Validation",    color: "#43b89c", statuts: ["SIGNED"] },
  { id: "sign",     icon: "🔏", label: "Signature",     color: "#7c3aed", statuts: ["SUBMITTED"] },
  { id: "ance",     icon: "🏛️", label: "ANCE",         color: "#f59e0b", statuts: ["ACCEPTED", "REJECTED"] },
  { id: "archive",  icon: "🗄️", label: "Archivage",    color: "#e11d48", statuts: ["ARCHIVED"] },
];

// ─── STATUT CONFIG ────────────────────────────────────────────
const STATUT_CONFIG = {
  C:         { label: "Créée",         color: "#2563eb", bg: "#dbeafe", icon: "📝" },
  V:         { label: "Validée",       color: "#16a34a", bg: "#dcfce7", icon: "✅" },
  SIGNED:    { label: "Signée",        color: "#7c3aed", bg: "#ede9fe", icon: "🔏" },
  SUBMITTED: { label: "Soumise ANCE",  color: "#0891b2", bg: "#cffafe", icon: "📤" },
  ACCEPTED:  { label: "Acceptée ANCE", color: "#16a34a", bg: "#dcfce7", icon: "🏛️" },
  REJECTED:  { label: "Rejetée",       color: "#dc2626", bg: "#fee2e2", icon: "❌" },
  PENDING:   { label: "En attente",    color: "#d97706", bg: "#fef3c7", icon: "⏳" },
  ARCHIVED:  { label: "Archivée",      color: "#e11d48", bg: "#ffe4e6", icon: "🗄️" },
};
const getStatut = (s) => STATUT_CONFIG[(s||"").toUpperCase()] || { label: s || "—", color: "#64748b", bg: "#f1f5f9", icon: "❓" };
const getDevise = (f) => f?.DeviseAbrege || "TND";

// ─── SIDEBAR NAV ──────────────────────────────────────────────
const NAV_SECTIONS = [
  {
    label: "Mon Dashboard", color: "#6c63ff",
    items: [
      { icon: "◈",  label: "Vue d'ensemble", tab: "dashboard" },
      { icon: "📊", label: "KPIs",            tab: "dashboard" },
    ],
  },
  {
    label: "ERP & Facturation", color: "#43b89c",
    items: [
      { icon: "✏️", label: "Créer Facture",  path: "/facture/create" },
    ],
  },
  {
    label: "Fonctions Avancées", color: "#f59e0b",
    items: [
      { icon: "🧪", label: "Sandbox ANCE",    tab: "sandbox"  },
    ],
  },
  {
    label: "Administration", color: "#e11d48",
    items: [
      { icon: "👤", label: "Users Société",   tab: "users"    },
      { icon: "🏢", label: "Sociétés",         tab: "societes" },
      { icon: "📬", label: "Mes Demandes", tab: "assignments" },
    ],
  },
  {
    label: "Paramètres", color: "#9b59b6",
    items: [
      { icon: "⚙️", label: "Mon Profil",      path: "/profile"   },
      { icon: "🔒", label: "Certificat",       tab: "parametres"  },
    ],
  },
  
];

const TABS = [
  { id: "dashboard",   label: "Vue d'ensemble", icon: "◈"   },
  { id: "sandbox",     label: "Sandbox ANCE",      icon: "🧪"  },
  { id: "users",       label: "Users",             icon: "👤"  },
  { id: "societes",    label: "Sociétés",           icon: "🏢"  },
  { id: "parametres",  label: "Paramètres",        icon: "⚙️"  },
  { id: "mes-societes", label: "Mes Sociétés" },
  { id: "assignments", label: "Mes Demandes", icon: "📬" },
];

// ─── Animated counter hook (identique à home.js) ─────────────
const useCounter = (target, duration = 1200) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!target || typeof target !== "number") return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [target]);
  return count;
};
const AnimCount = ({ n }) => { const c = useCounter(n); return c; };

// ─── Export helpers (identique à home.js) ────────────────────
const exportCSV = (factures) => {
  const headers = ["N° Facture","N° Comptable","Client","Date","HT (TND)","TVA (TND)","TTC (TND)","Statut"];
  const rows = factures.map(f => [
    f.NumfactAffich||f.NumFacture, f.NumFactureComptable||"",
    f.NomClient||f.CodeClient||"",
    f.DateFacture ? new Date(f.DateFacture).toLocaleDateString("fr-FR") : "",
    f.MontantTotalHT  != null ? Number(f.MontantTotalHT).toFixed(3)  : "",
    f.MontantTVA1     != null ? Number(f.MontantTVA1).toFixed(3)     : "",
    f.MontantTotalTTC != null ? Number(f.MontantTotalTTC).toFixed(3) : "",
    getStatut(f.StatutCommande||f.Statut||f.status).label,
  ]);
  const csv  = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a"); a.href = url;
  a.download = `factures_admin_${new Date().toISOString().slice(0,10)}.csv`; a.click();
  URL.revokeObjectURL(url);
};

const exportExcel = (factures) => {
  const headers = ["N° Facture","N° Comptable","Client","Date","HT (TND)","TVA (TND)","TTC (TND)","Statut"];
  const rows = factures.map(f => [
    f.NumfactAffich||f.NumFacture, f.NumFactureComptable||"—",
    f.NomClient||f.CodeClient||"—",
    f.DateFacture ? new Date(f.DateFacture).toLocaleDateString("fr-FR") : "—",
    f.MontantTotalHT  != null ? Number(f.MontantTotalHT).toFixed(3)  : "—",
    f.MontantTVA1     != null ? Number(f.MontantTVA1).toFixed(3)     : "—",
    f.MontantTotalTTC != null ? Number(f.MontantTotalTTC).toFixed(3) : "—",
    getStatut(f.StatutCommande||f.Statut||f.status).label,
  ]);
  const html = `<table><tr>${headers.map(h=>`<th>${h}</th>`).join("")}</tr>${rows.map(r=>`<tr>${r.map(v=>`<td>${v}</td>`).join("")}</tr>`).join("")}</table>`;
  const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a"); a.href = url;
  a.download = `factures_admin_${new Date().toISOString().slice(0,10)}.xls`; a.click();
  URL.revokeObjectURL(url);
};

// ═════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═════════════════════════════════════════════════════════════
export default function Admin() {
  const navigate  = useNavigate();
  const location  = useLocation();

  // ── UI State ──
  const [activeTab,        setActiveTab]        = useState("dashboard");
  const [tabHistory, setTabHistory] = useState([]);

const goToTab = (newTab) => {
  setTabHistory(prev => [...prev, activeTab]);
  setActiveTab(newTab);
};
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [toast,            setToast]            = useState(null);

  // ── User ──
  const [currentUser, setCurrentUser] = useState("Admin");
  const [societeNom,  setSocieteNom]  = useState("LOGIMES");
  const [societeLogo, setSocieteLogo] = useState(null);
  const [currentRole, setCurrentRole] = useState("");
  const [currentSocieteId, setCurrentSocieteId] = useState(null);

  // ── Data ──
  const [factures,     setFactures]     = useState([]);
  const [clients,      setClients]      = useState([]);
  const [archive,      setArchive]      = useState([]);
  const [archiveStats, setArchiveStats] = useState(null);
  const [alerts,       setAlerts]       = useState([]);
  const [tracabilite,  setTracabilite]  = useState(null);
  const [avoirs,       setAvoirs]       = useState([]);
  const [offlineQueue, setOfflineQueue] = useState([]);
  const [certStatus,   setCertStatus]   = useState(null);
  const [sandboxRes,   setSandboxRes]   = useState(null);
  const [loading,      setLoading]      = useState(true);

  // ── Modification Requests (Admin → Super Admin workflow) ──
const [modModalOpen, setModModalOpen] = useState(false);
const [modTargetUser, setModTargetUser] = useState(null);
const [modForm, setModForm] = useState({ field: 'name', newValue: '' });
const [modLoading, setModLoading] = useState(false);
const [modRequests, setModRequests] = useState([]); // Pour Super Admin: liste des demandes
const [modReqLoading, setModReqLoading] = useState(false);
const [modActionLoading, setModActionLoading] = useState({});

// ── Assignments ──
const [assignments,   setAssignments]  = useState([]);
const [assignLoading, setAssignLoading] = useState(false);


  // ── Users & Sociétés ──
  const [companyUsers,    setCompanyUsers]    = useState([]);
  const [societes,        setSocietes]        = useState([]);
  const [pendingSocietes, setPendingSocietes] = useState([]);
  const [usersLoading,    setUsersLoading]    = useState(false);
  const [socLoading,      setSocLoading]      = useState(false);

  // ── Filtres avancés (comme home.js) ──
  const [search,           setSearch]           = useState("");
  const [filterStatut,     setFilterStatut]     = useState("all");
  const [filterDateFrom,   setFilterDateFrom]   = useState("");
  const [filterDateTo,     setFilterDateTo]     = useState("");
  const [filterMontantMin, setFilterMontantMin] = useState("");
  const [filterMontantMax, setFilterMontantMax] = useState("");
  const [filterClientQ,    setFilterClientQ]    = useState("");
  const [showAdvFilters,   setShowAdvFilters]   = useState(false);
  const [exportMenuOpen,   setExportMenuOpen]   = useState(false);
  const exportMenuRef = useRef(null);

  // ── Notifications (identique home.js) ──
  const [notifications, setNotifications] = useState([]);
  const [notifOpen,     setNotifOpen]     = useState(false);
  const [unreadCount,   setUnreadCount]   = useState(0);
  const notifRef       = useRef(null);
  const pollingRef     = useRef(null);
  const prevFactRef    = useRef([]);

  // ── Retry & XML & PDF loading ──
  const [retryLoading,  setRetryLoading]  = useState({});
  const [xmlLoading,    setXmlLoading]    = useState({});
  const [pdfLoading,    setPdfLoading]    = useState({});
  const [verifyResults, setVerifyResults] = useState({});

  // ── DSS ──
const [dssNumFacture, setDssNumFacture] = useState("");
const [dssLoading,    setDssLoading]    = useState(false);
const [dssResult,     setDssResult]     = useState(null);
const [dssHistory,    setDssHistory]    = useState([]);

// ── Tracabilité ──
const [selectedFact, setSelectedFact] = useState(null);
const [traceData,    setTraceData]    = useState(null);
const [traceLoading, setTraceLoading] = useState(false);
const [traceSearch,  setTraceSearch]  = useState("");

  // ── Sandbox (USE CASE 2: Tester sandbox ANCE) ──
  const [sbNumFacture, setSbNumFacture] = useState("");
  const [sbLoading,    setSbLoading]    = useState(false);
  const [sbEnv,        setSbEnv]        = useState("sandbox");

  // ── Valider inscription (USE CASE 4) ──
  const [activateLoading, setActivateLoading] = useState({});

  // ── Modal détail facture (comme home.js) ──
  const [detailModal, setDetailModal] = useState(null);

  const [sysConfig, setSysConfig] = useState({});

  

  // ─── INIT ─────────────────────────────────────────────────
  useEffect(() => {
    const role = getCookie("role");
    if (role !== "admin") { navigate("/home"); return; }
    const name  = getCookie("name");
    const sid   = getCookie("societe_id");
    const soc   = getCookie("societe");
    if (name) setCurrentUser(name);
    if (soc)  setSocieteNom(soc);
    const logo = localStorage.getItem(`logo_${sid}`);
    if (logo) setSocieteLogo(logo);

    const savedNotifs = localStorage.getItem("logimes_admin_notifs");
    if (savedNotifs) {
      try {
        const p = JSON.parse(savedNotifs);
        setNotifications(p);
        setUnreadCount(p.filter(n => !n.read).length);
      } catch {}
    }

    fetchSysConfig();
    fetchAllData();
    fetchCompanyUsers();
    fetchSocietes();
    fetchMyAssignments();

    const handleClick = (e) => {
      if (notifRef.current      && !notifRef.current.contains(e.target))      setNotifOpen(false);
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) setExportMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  useEffect(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(() => checkForUpdates(), 30000);
    return () => clearInterval(pollingRef.current);
  }, []);

  // ─── HELPERS ──────────────────────────────────────────────
  const token     = () => getCookie("token");
  const authH     = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${token()}` });
const sid = () => getCookie("societe_folder") || getCookie("societe_id") || "";
const q   = () => sid() ? `?societe_id=${sid()}` : "";

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const addNotification = useCallback((notif) => {
    setNotifications(prev => {
      const updated = [{ ...notif, id: Date.now(), time: new Date(), read: false }, ...prev].slice(0, 50);
      localStorage.setItem("logimes_admin_notifs", JSON.stringify(updated));
      return updated;
    });
    setUnreadCount(prev => prev + 1);
  }, []);

  const markAllRead = () => {
    setNotifications(prev => {
      const updated = prev.map(n => ({ ...n, read: true }));
      localStorage.setItem("logimes_admin_notifs", JSON.stringify(updated));
      return updated;
    });
    setUnreadCount(0);
  };

  const clearNotifications = () => {
    setNotifications([]); setUnreadCount(0);
    localStorage.removeItem("logimes_admin_notifs");
  };

  const getStatutField = (f) => (f.StatutCommande||f.Statut||f.status||"").toUpperCase();
  const getWorkflowStep = (s) => {
    const idx = WORKFLOW_STEPS.findIndex(step => step.statuts.includes((s||"").toUpperCase()));
    return idx >= 0 ? idx : 0;
  };

  // ─── POLLING (identique home.js) ──────────────────────────
  const checkForUpdates = useCallback(async () => {
    try {
      const url = sid() ? `${MIDDLEWARE_URL}/factures?societe_id=${sid()}&limit=200` : `${MIDDLEWARE_URL}/factures?limit=200`;
      const res  = await fetch(url, { headers: authH() });
      const data = await res.json();
      const newFacts = (data.factures || data || []).map(f => ({
        ...f, Statut: localStorage.getItem(`facture_statut_${f.NumFacture}`) || f.StatutCommande || null
      }));
      const prev = prevFactRef.current;
      newFacts.forEach(nf => {
        const old = prev.find(p => p.NumFacture === nf.NumFacture);
        if (old && old.Statut !== nf.Statut) {
          addNotification({
            type: nf.Statut === "REJECTED" ? "error" : nf.Statut === "ACCEPTED" ? "success" : "info",
            title: `Facture #${nf.NumfactAffich||nf.NumFacture} mise à jour`,
            message: `${getStatut(old.Statut).icon} → ${getStatut(nf.Statut).icon} ${getStatut(nf.Statut).label}`,
          });
        }
      });
      prevFactRef.current = newFacts;
      setFactures(newFacts);

      // Check nouvelles inscriptions
      const r2   = await fetch(`${BACKEND_URL}/api/auth/pending`, { headers: authH() });
      const d2   = await r2.json();
      const pend = Array.isArray(d2) ? d2 : d2.users || [];
      if (pend.length > pendingSocietes.length) {
        addNotification({ type: "info", title: "Nouvelle inscription", message: `${pend.length} société(s) en attente de validation` });
      }
      setPendingSocietes(pend);
    } catch {}
  }, [addNotification]);

  // ─── FETCH ALL ────────────────────────────────────────────
  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [factData, clientData, archData, archStats,
             alertData, traceData, avoirData, offData, certData, sbData] =
        await Promise.all([
          fetch(`${MIDDLEWARE_URL}/factures${q()}`,              { headers: authH() }).then(r=>r.json()).catch(()=>[]),
          fetch(`${MIDDLEWARE_URL}/clients`,                     { headers: authH() }).then(r=>r.ok?r.json():[]).catch(()=>[]),
          fetch(`${MIDDLEWARE_URL}/archive/invoices${q()}`,      { headers: authH() }).then(r=>r.json()).catch(()=>[]),
          fetch(`${MIDDLEWARE_URL}/archive/statistics${q()}`,    { headers: authH() }).then(r=>r.json()).catch(()=>null),
          fetch(`${MIDDLEWARE_URL}/tracabilite/alerts${q()}`,    { headers: authH() }).then(r=>r.json()).catch(()=>[]),
          fetch(`${MIDDLEWARE_URL}/tracabilite/dashboard${q()}`, { headers: authH() }).then(r=>r.json()).catch(()=>null),
          fetch(`${MIDDLEWARE_URL}/avoirs${q()}`,                { headers: authH() }).then(r=>r.json()).catch(()=>[]),
          fetch(`${MIDDLEWARE_URL}/offline/queue${q()}`,         { headers: authH() }).then(r=>r.json()).catch(()=>[]),
          fetch(`${MIDDLEWARE_URL}/certificate/status${q()}`,   { headers: authH() }).then(r=>r.json()).catch(()=>null),
          fetch(`${MIDDLEWARE_URL}/ance/sandbox/results${q()}`,  { headers: authH() }).then(r=>r.json()).catch(()=>null),
        ]);

      const rawFacts = (Array.isArray(factData) ? factData : factData?.factures || []).map(f => ({
        ...f, Statut: localStorage.getItem(`facture_statut_${f.NumFacture}`) || f.StatutCommande || null
      }));
      prevFactRef.current = rawFacts;
      setFactures(rawFacts);
      setClients(Array.isArray(clientData) ? clientData : clientData?.clients || []);
      setArchive(Array.isArray(archData) ? archData : archData?.invoices || []);
      setArchiveStats(archStats?.statistics || archStats);
      setAlerts(Array.isArray(alertData) ? alertData : alertData?.alerts || []);
      setTracabilite(traceData);
      setAvoirs(Array.isArray(avoirData) ? avoirData : avoirData?.avoirs || []);
      setOfflineQueue(Array.isArray(offData) ? offData : offData?.queue || []);
      setCertStatus(certData);
      setSandboxRes(sbData);

      // Charger historique DSS
      const dssRes = await fetch(`${MIDDLEWARE_URL}/factures/dss/history${q()}`, { headers: authH() }).catch(()=>null);
      if (dssRes?.ok) { const d = await dssRes.json(); setDssHistory(d.submissions || d || []); }
    } catch { showToast("Erreur chargement données", "error"); }
    setLoading(false);
  };


  const fetchSysConfig = async () => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/auth/config`, { headers: authH() });
    const data = await res.json();
    setSysConfig(data.config || {});
  } catch {}
};
  // ─── FETCH USERS ──────────────────────────────────────────
    const fetchCompanyUsers = async () => {
      setUsersLoading(true);
      try {
        // استعمل /admin/users بدل /company/users
        const res = await fetch(`${BACKEND_URL}/api/auth/admin/users`, { 
          headers: authH() 
        });
        const data = await res.json();
        setCompanyUsers(Array.isArray(data) ? data : data.users || []);
      } catch {}
      setUsersLoading(false);
    };
  // ─── FETCH SOCIETES ───────────────────────────────────────
  const fetchSocietes = async () => {
    setSocLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${BACKEND_URL}/api/auth/societes`, { headers: authH() }),
        fetch(`${BACKEND_URL}/api/auth/pending`,  { headers: authH() }),
      ]);
      const d1 = await r1.json(); setSocietes(Array.isArray(d1) ? d1 : d1.societes || []);
      const d2 = await r2.json(); setPendingSocietes(Array.isArray(d2) ? d2 : d2.users || []);
    } catch {}
    setSocLoading(false);
  };

  const fetchMyAssignments = async () => {
  setAssignLoading(true);
  try {
    const res  = await fetch(`${BACKEND_URL}/api/auth/assignments/mine`, { 
      headers: authH() 
    });
    const data = await res.json();
    setAssignments(data.assignments || []);
    const pending = (data.assignments || []).filter(a => a.statut === "PENDING");
    if (pending.length > 0) {
      addNotification({
        type: "info",
        title: `${pending.length} demande(s) en attente`,
        message: "Le Super Admin vous a assigné des sociétés à gérer"
      });
    }
  } catch { showToast("Erreur chargement demandes", "error"); }
  setAssignLoading(false);
};

  const fetchMySocietes = async () => {
  try {
    const res = await fetch("/api/admin/my-societes", {
      headers: authH()
    });
    const data = await res.json();
    setSocietes(data);
  } catch (err) {
    console.error("Erreur fetch societes:", err);
  }
};

  // ══════════════════════════════════════════════════════════
  // USE CASE 1 — SUPERVISER LES SOUMISSIONS DSS
  // ══════════════════════════════════════════════════════════
  const handleDssSubmit = async () => {
    if (!dssNumFacture.trim()) { showToast("Entrez un numéro de facture", "error"); return; }
    setDssLoading(true); setDssResult(null);
    try {
      const url  = `${MIDDLEWARE_URL}/factures/${dssNumFacture}/submit-dss${q() ? q() : ""}`;
      const res  = await fetch(url, { method: "POST", headers: authH() });
      const data = await res.json();
      setDssResult(data);
      if (data.success) {
        const refTTN = data.reference_ttn || data.uuid_fiscal || "";
        if (refTTN) localStorage.setItem(`facture_refttn_${dssNumFacture}`, refTTN);
        showToast(`✅ Facture #${dssNumFacture} soumise à ANCE !`);
        addNotification({ type: "success", title: `DSS soumis — #${dssNumFacture}`, message: refTTN ? `TTN: ${refTTN.slice(0,30)}...` : "Soumission réussie" });
        setDssHistory(prev => [{ invoice_number: dssNumFacture, status: "SUBMITTED", created_at: new Date().toISOString(), uuid_dss: refTTN }, ...prev].slice(0, 20));
      } else {
        showToast(data.detail || "Erreur soumission DSS", "error");
      }
    } catch { showToast("Erreur réseau DSS", "error"); }
    setDssLoading(false);
  };

  // ── Retry ANCE (depuis tab factures) ──────────────────────
  const handleRetry = async (numFacture) => {
    setRetryLoading(prev => ({ ...prev, [numFacture]: true }));
    try {
      showToast(`⏳ Retry en cours — #${numFacture}...`);
      const res  = await fetch(`${MIDDLEWARE_URL}/retry/manual/${numFacture}${q() ? q() : ""}`, { method: "POST", headers: authH() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.message || "Erreur retry");
      const newStatut = data.new_status || data.statut || "SUBMITTED";
      localStorage.setItem(`facture_statut_${numFacture}`, newStatut);
      setFactures(prev => prev.map(f => f.NumFacture === numFacture ? { ...f, Statut: newStatut } : f));
      addNotification({ type: "success", title: `Retry réussi — #${numFacture}`, message: `Facture resoumise à l'ANCE` });
      showToast(`✅ Retry réussi — Facture #${numFacture} resoumise`);
    } catch (err) { showToast(`❌ Retry échoué: ${err.message}`, "error"); }
    setRetryLoading(prev => ({ ...prev, [numFacture]: false }));
  };

  // ── Workflow actions (Valider / Signer / Soumettre DSS) ───
  const handleAction = async (numFacture, action, label) => {
    try {
      showToast(`⏳ ${label} facture #${numFacture}...`);
      const res  = await fetch(`${MIDDLEWARE_URL}/factures/${numFacture}/${action}${q() ? q() : ""}`, { method: "POST", headers: authH() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.message || `Erreur ${label}`);
      const newStatut = { "validate": "V", "sign": "SIGNED", "submit-dss": "ARCHIVED" }[action];
      if (newStatut) {
        localStorage.setItem(`facture_statut_${numFacture}`, newStatut);
        setFactures(prev => prev.map(f => f.NumFacture === numFacture ? { ...f, Statut: newStatut } : f));
      }
      if (action === "submit-dss") {
        const refTTN = data.reference_ttn || data.uuid_fiscal || "";
        if (refTTN) localStorage.setItem(`facture_refttn_${numFacture}`, refTTN);
      }
      addNotification({ type: "success", title: `Facture #${numFacture} — ${label}`, message: `Action effectuée avec succès` });
      showToast(`✅ Facture #${numFacture} — ${label} réussi !`);
    } catch (e) { showToast(`❌ ${e.message}`, "error"); }
  };

  // ── PDF & XML ─────────────────────────────────────────────
  const downloadPDF = async (numFacture) => {
    setPdfLoading(prev => ({ ...prev, [numFacture]: true }));
    try {
      const res  = await fetch(`${MIDDLEWARE_URL}/factures/${numFacture}/pdf${q() ? q() : ""}`, { headers: authH() });
      if (!res.ok) throw new Error("PDF non disponible");
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement("a"); a.href = url;
      a.download = `facture_${numFacture}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      showToast(`📄 PDF téléchargé — #${numFacture}`);
    } catch (e) { showToast(`❌ ${e.message}`, "error"); }
    setPdfLoading(prev => ({ ...prev, [numFacture]: false }));
  };

  const downloadXML = async (numFacture) => {
    setXmlLoading(prev => ({ ...prev, [numFacture]: true }));
    try {
      const res  = await fetch(`${MIDDLEWARE_URL}/archive/download/${numFacture}${q() ? q() : ""}`, { headers: authH() });
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const blob = await res.blob();
      if (blob.size === 0) throw new Error("XML vide");
      const url  = window.URL.createObjectURL(new Blob([blob], { type: "application/xml" }));
      const a    = document.createElement("a"); a.href = url;
      a.download = `facture_${numFacture}_TEIF.xml`; a.click();
      window.URL.revokeObjectURL(url);
      showToast(`✅ XML téléchargé — #${numFacture}`);
    } catch (err) { showToast(`❌ Erreur XML: ${err.message}`, "error"); }
    setXmlLoading(prev => ({ ...prev, [numFacture]: false }));
  };

  const verifyIntegrity = async (numFacture) => {
    setVerifyResults(prev => ({ ...prev, [numFacture]: { loading: true } }));
    try {
      const res  = await fetch(`${MIDDLEWARE_URL}/archive/invoices/${numFacture}/integrity${q() ? q() : ""}`, { headers: authH() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Erreur vérification");
      setVerifyResults(prev => ({
        ...prev, [numFacture]: { loading: false, valid: data.valid ?? data.integrity_valid ?? false,
          hash: data.stored_hash || data.hash_sha256 || "—", message: data.message || (data.valid ? "Intégrité confirmée" : "Hash invalide") }
      }));
      showToast(data.valid ? `✅ Intégrité vérifiée — #${numFacture}` : `⚠️ Hash invalide — #${numFacture}`, data.valid ? "success" : "error");
    } catch (err) {
      setVerifyResults(prev => ({ ...prev, [numFacture]: { loading: false, error: err.message } }));
      showToast(`❌ Erreur vérification`, "error");
    }
  };

  // ─── FETCH TRACABILITÉ ────────────────────────────────────
  const fetchTrace = async (numFacture) => {
    setSelectedFact(numFacture); setTraceLoading(true); setTraceData(null); setActiveTab("tracabilite");
    try {
      const [statusRes, histRes] = await Promise.all([
        fetch(`${MIDDLEWARE_URL}/tracabilite/${numFacture}/status`,  { headers: authH() }),
        fetch(`${MIDDLEWARE_URL}/tracabilite/${numFacture}/history`, { headers: authH() }),
      ]);
      setTraceData({ status: await statusRes.json(), history: (await histRes.json()).history || [] });
    } catch { setTraceData({ error: "Erreur traçabilité" }); }
    setTraceLoading(false);
  };

  // ══════════════════════════════════════════════════════════
  // USE CASE 2 — TESTER SANDBOX ANCE
  // ══════════════════════════════════════════════════════════
  const handleSandboxSubmit = async () => {
    if (!sbNumFacture.trim()) { showToast("Entrez un numéro de facture", "error"); return; }
    setSbLoading(true);
    try {
      const url  = `${MIDDLEWARE_URL}/ance/sandbox/submit/${sbNumFacture}${q() ? q()+"&" : "?"}environment=${sbEnv}`;
      const res  = await fetch(url, { method: "POST", headers: authH() });
      const data = await res.json();
      if (data.success) {
        showToast(`✅ Sandbox OK — UUID: ${data.uuid_fiscal || data.uuid || ""}`);
        addNotification({ type: "success", title: `Sandbox ANCE — #${sbNumFacture}`, message: `Test réussi (${sbEnv})` });
        fetchAllData();
      } else {
        showToast(data.detail || "Erreur sandbox", "error");
      }
    } catch { showToast("Erreur réseau sandbox", "error"); }
    setSbLoading(false);
  };

  // ══════════════════════════════════════════════════════════
  // USE CASE 3 — MODIFIER INFOS SENSIBLES SOCIÉTÉ
  // Étape 1 : admin soumet la demande de modification
  // Étape 2 : admin valide et confirme la modification
  // ══════════════════════════════════════════════════════════
  // ══════════════════════════════════════════════════════════
  // USE CASE 4 — VALIDER INSCRIPTION SOCIÉTÉ
  // ══════════════════════════════════════════════════════════
  const handleActivate = async (userId) => {
    setActivateLoading(prev => ({ ...prev, [userId]: true }));
    try {
      const res  = await fetch(`${BACKEND_URL}/api/auth/admin/activate/${userId}`, { method: "PUT", headers: authH() });
      const data = await res.json();
      if (res.ok) {
        showToast("✅ Compte activé — Société validée !");
        addNotification({ type: "success", title: "Inscription validée", message: `Société activée avec succès` });
        fetchSocietes();
      } else {
        showToast(data.error || "Erreur activation", "error");
      }
    } catch { showToast("Erreur réseau", "error"); }
    setActivateLoading(prev => ({ ...prev, [userId]: false }));
  };

  const handleRejectActivation = async (userId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/admin/reject/${userId}`, { method: "PUT", headers: authH() });
      if (res.ok) { showToast("🚫 Inscription rejetée"); fetchSocietes(); }
      else { const d = await res.json(); showToast(d.error || "Erreur rejet", "error"); }
    } catch { showToast("Erreur réseau", "error"); }
  };

  // ── Sync offline ──────────────────────────────────────────
  const handleSync = async () => {
    try {
      const res  = await fetch(`${MIDDLEWARE_URL}/offline/sync${q() ? q() : ""}`, { method: "POST", headers: authH() });
      const data = await res.json();
      showToast(`✅ Sync: ${data.synced || 0} factures synchronisées`);
      fetchAllData();
    } catch { showToast("Erreur sync", "error"); }
  };

  // ── Open Modifier Modal (Admin) ──
const openModifierModal = (user) => {
  console.log("User ID:", user.id, typeof user.id);
  setModTargetUser(user);
  setModForm({ field: 'name', newValue: user.name || '' });
  setModModalOpen(true);
};

// ── Send Modification Request (Admin → Backend) ──
const sendModificationRequest = async () => {
  if (!modTargetUser || !modForm.newValue?.trim()) {
    showToast("Veuillez remplir la nouvelle valeur", "error");
    return;
  }

  try {
    setModLoading(true);

    const payload = {
      user_id: parseInt(modTargetUser.id),
      field_name: modForm.field,
      new_value: modForm.newValue.trim()
    };

    console.log("📤 Envoi du payload :", payload);   // مهم للـ debug

    const res = await fetch(`${BACKEND_URL}/api/auth/modification-requests`, {
      method: "POST",
      headers: authH(),
      body: JSON.stringify(payload)
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || "Erreur envoi de la demande");
    }

    showToast("✅ Demande envoyée au Super Admin !", "success");

    addNotification({
      type: "info",
      title: "Demande de modification",
      message: `Modification de ${modForm.field} pour ${modTargetUser.name || modTargetUser.nom} en attente`
    });

    setModModalOpen(false);
    setModForm({ field: 'name', newValue: '' });

  } catch (err) {
    console.error("❌ Erreur complète:", err);
    showToast("❌ " + err.message, "error");
  } finally {
    setModLoading(false);
  }
};

// ── Fetch Pending Requests (Super Admin) ──
const fetchModificationRequests = async () => {
  try {
    setModReqLoading(true);
    const res = await fetch(`${BACKEND_URL}/api/auth/modification-requests?status=pending`, {
      headers: authH()
    });
    const data = await res.json();
    setModRequests(data.requests || []);
  } catch (err) {
    console.error("❌ Error fetching requests", err);
  } finally {
    setModReqLoading(false);
  }
};

// ── Approve/Reject Request (Super Admin) ──
const handleModRequestAction = async (requestId, action, reason = "") => {
  setModActionLoading(prev => ({ ...prev, [requestId]: true }));
  try {
    const res = await fetch(`${BACKEND_URL}/api/auth/modification-requests/${requestId}/${action}`, {
      method: "PUT",
      headers: authH(),
      body: JSON.stringify({ reason })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    
    showToast(action === "approve" ? "✅ Modification approuvée" : "❌ Modification rejetée");
    fetchModificationRequests(); // Refresh list
    fetchCompanyUsers(); // Refresh users list si approved
  } catch (err) {
    showToast("❌ " + err.message, "error");
  } finally {
    setModActionLoading(prev => ({ ...prev, [requestId]: false }));
  }
};

  // ─── COMPUTED ─────────────────────────────────────────────
  const certOk = certStatus?.valid || certStatus?.status === "valid" || certStatus?.certificate_info?.status === "VALID";
  const initials = currentUser.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) || "AD";

  const filteredFactures = factures.filter(f => {
    const q2      = search.toLowerCase();
    const cq      = filterClientQ.toLowerCase();
    const montant = f.MontantTotalTTC != null ? Number(f.MontantTotalTTC) : null;
    const dateF   = f.DateFacture ? new Date(f.DateFacture) : null;
    const statut  = getStatutField(f);
    return (
      (!q2      || String(f.NumfactAffich||f.NumFacture).includes(q2) || String(f.NomClient||f.CodeClient||"").toLowerCase().includes(q2)) &&
      (!cq      || String(f.NomClient||f.CodeClient||"").toLowerCase().includes(cq) || String(f.CodeClient||"").toLowerCase().includes(cq)) &&
      (filterStatut === "all" || statut === filterStatut) &&
      (!filterDateFrom   || (dateF && dateF >= new Date(filterDateFrom))) &&
      (!filterDateTo     || (dateF && dateF <= new Date(filterDateTo + "T23:59:59"))) &&
      (!filterMontantMin || (montant != null && montant >= Number(filterMontantMin))) &&
      (!filterMontantMax || (montant != null && montant <= Number(filterMontantMax)))
    );
  });
  const hasAdvFilters = filterDateFrom || filterDateTo || filterMontantMin || filterMontantMax || filterClientQ;
  const resetFilters  = () => { setSearch(""); setFilterStatut("all"); setFilterDateFrom(""); setFilterDateTo(""); setFilterMontantMin(""); setFilterMontantMax(""); setFilterClientQ(""); };
  const totalTTCFiltre = filteredFactures.reduce((acc, f) => acc + (f.MontantTotalTTC != null ? Number(f.MontantTotalTTC) : 0), 0);

  const quickStats = {
    total:    factures.length,
    accepted: factures.filter(f => ["ACCEPTED","ARCHIVED","CREDITED"].includes(getStatutField(f))).length,
    pending:  factures.filter(f => ["C","V","SIGNED","SUBMITTED","PENDING"].includes(getStatutField(f))).length,
    rejected: factures.filter(f => getStatutField(f) === "REJECTED").length,
  };

  const handleNavClick = (item) => {
    if (item.tab)  setActiveTab(item.tab);
    else if (item.path) navigate(item.path);
  };

  // ══════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════
  return (
    <div style={S.root}>
      <style>{css}</style>

      {/* ════════ SIDEBAR ════════ */}
      <aside style={{ ...S.sidebar, width: sidebarCollapsed ? 60 : 224 }}>
        <div style={S.brand} onClick={() => navigate("/home")}>
          <div style={S.brandMark}>
            {societeLogo
              ? <img src={societeLogo} alt="logo" style={{ width:"100%",height:"100%",objectFit:"cover",borderRadius:9 }} />
              : <span style={{ fontSize:"1.1rem" }}>⚡</span>}
          </div>
          {!sidebarCollapsed && <span style={S.brandName}>LOGIMES</span>}
        </div>

        <button style={S.collapseBtn} onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
          {sidebarCollapsed ? "→" : "←"}
        </button>

        <nav style={S.sidebarNav}>
          <div style={{ ...S.navItem, background: activeTab==="dashboard"?"#6c63ff18":"transparent", color: activeTab==="dashboard"?"#6c63ff":"#64748b", borderLeft: activeTab==="dashboard"?"3px solid #6c63ff":"3px solid transparent" }}
            onClick={() => setActiveTab("dashboard")}>
            <span style={S.navIcon}>🏠</span>
            {!sidebarCollapsed && <span style={S.navLabel}>Accueil Admin</span>}
          </div>

          {NAV_SECTIONS.map(sec => (
            <div key={sec.label}>
              {!sidebarCollapsed && (
                <div style={{ ...S.sectionLabel, color: sec.color }}>
                  <span style={{ ...S.sectionDot, background: sec.color }} />{sec.label}
                </div>
              )}
              {sidebarCollapsed && <div style={S.sectionDivider} />}
              {sec.items.map(item => {
                const isActive = item.tab ? activeTab === item.tab : location.pathname === item.path;
                return (
                  <div key={item.label}
                    style={{ ...S.navItem, background: isActive?sec.color+"18":"transparent", color: isActive?sec.color:"#64748b", borderLeft: isActive?`3px solid ${sec.color}`:"3px solid transparent" }}
                    className={isActive ? "" : "nav-hover"}
                    onClick={() => handleNavClick(item)}
                    title={sidebarCollapsed ? item.label : ""}>
                    <span style={S.navIcon}>{item.icon}</span>
                    {!sidebarCollapsed && <span style={S.navLabel}>{item.label}</span>}
                    {!sidebarCollapsed && item.tab === "societes" && pendingSocietes.length > 0 && (
                      <span style={{ background:"#d97706", color:"#fff", fontSize:"0.55rem", fontWeight:800, padding:"1px 5px", borderRadius:20, marginLeft:"auto" }}>{pendingSocietes.length}</span>
                    )}
                    {!sidebarCollapsed && item.tab === "tracabilite" && alerts.length > 0 && (
                      <span style={{ background:"#ef4444", color:"#fff", fontSize:"0.55rem", fontWeight:800, padding:"1px 5px", borderRadius:20, marginLeft:"auto" }}>{alerts.length}</span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </nav>

        <div style={S.sidebarFooter}>
          {!sidebarCollapsed && (
            <div style={S.userCard}>
              <div style={S.userInitials}>{initials}</div>
              <div style={S.userMeta}>
                <div style={S.userNameText}>{currentUser}</div>
                <div style={{ fontSize:"0.63rem", color:"#6c63ff", fontWeight:600 }}>▸ Administrateur</div>
              </div>
            </div>
          )}
          <button style={S.logoutBtn} className="logout-hover" title="Déconnexion"
            onClick={() => {
              ["token","role","name","societe_id","societe_folder"].forEach(k =>
                document.cookie = `${k}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
              );
              navigate("/");
            }}>
            🚪 {!sidebarCollapsed && "Déconnexion"}
          </button>
        </div>
      </aside>

      {/* ════════ MAIN ════════ */}
      <main style={{ ...S.main, marginLeft: sidebarCollapsed ? 60 : 224 }}>

        {/* ── HEADER ── */}
        <header style={S.header}>
          <div>
            {tabHistory.length > 0 && (
              <button
                style={{ 
                  background:"none", border:"none", cursor:"pointer",
                  fontSize:"0.75rem", fontWeight:600, color:"#6c63ff",
                  fontFamily:"inherit", padding:"0 0 4px",
                  display:"flex", alignItems:"center", gap:4
                }}
                onClick={() => {
                  const prev = tabHistory[tabHistory.length - 1];
                  setTabHistory(h => h.slice(0, -1));
                  setActiveTab(prev);
                }}>
                ← Retour
              </button>
            )}
            <div style={S.headerEyebrow}>Administration · {societeNom}</div>
            <h1 style={S.headerTitle}>
              {activeTab === "dashboard"   && "Admin Dashboard"}
              {activeTab === "sandbox"     && "Sandbox ANCE — Tests"}
              {activeTab === "users"       && "Users Société"}
              {activeTab === "societes"    && "Gestion Sociétés"}
              {activeTab === "parametres"  && "Paramètres & Certificat"}
              {activeTab === "assignments" && "Mes Demandes de Gestion"}
            </h1>
          </div>

          <div style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 12px", borderRadius:20, background:"#f0fdf4", border:"1px solid #bbf7d0", fontSize:"0.72rem", fontWeight:700, color:"#16a34a" }}>
            <div style={{ width:7, height:7, borderRadius:"50%", background:"#16a34a" }} />
            🟢 Online
          </div>

          <div style={S.headerActions}>
            {/* Notifications bell */}
            <div style={{ position:"relative" }} ref={notifRef}>
              <button style={S.notifBtn} onClick={() => { setNotifOpen(!notifOpen); if (!notifOpen) markAllRead(); }}>
                <span style={{ fontSize:"1.1rem" }}>🔔</span>
                {unreadCount > 0 && <span style={S.notifBadge} className="notif-pulse">{unreadCount > 9 ? "9+" : unreadCount}</span>}
              </button>
              {notifOpen && (
                <div style={S.notifPanel}>
                  <div style={S.notifHeader}>
                    <span style={{ fontWeight:700, fontSize:"0.88rem", color:"#0F172A" }}>🔔 Notifications Admin</span>
                    <button style={S.notifAction} onClick={clearNotifications}>Effacer tout</button>
                  </div>
                  <div style={S.notifList}>
                    {notifications.length === 0 ? (
                      <div style={S.notifEmpty}><div style={{ fontSize:"1.5rem", marginBottom:6 }}>🔕</div><div style={{ fontSize:"0.78rem", color:"#94A3B8" }}>Aucune notification</div></div>
                    ) : notifications.map(n => (
                      <div key={n.id} style={{ ...S.notifItem, background: n.read?"#fff":"#f0efff", borderLeft:`3px solid ${n.type==="error"?"#dc2626":n.type==="success"?"#16a34a":"#6c63ff"}` }}>
                        <div style={{ display:"flex", alignItems:"flex-start", gap:8 }}>
                          <span style={{ fontSize:"1rem", flexShrink:0 }}>{n.type==="error"?"❌":n.type==="success"?"✅":"ℹ️"}</span>
                          <div style={{ flex:1 }}>
                            <div style={{ fontWeight:700, fontSize:"0.78rem", color:"#0F172A", marginBottom:2 }}>{n.title}</div>
                            <div style={{ fontSize:"0.72rem", color:"#64748B" }}>{n.message}</div>
                          </div>
                        </div>
                        <div style={{ fontSize:"0.65rem", color:"#94A3B8", marginTop:4 }}>
                          {n.time ? new Date(n.time).toLocaleString("fr-FR") : "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {alerts.length > 0 && (
              <span style={{ ...S.pill, background:"#fee2e2", color:"#dc2626", cursor:"pointer" }} onClick={() => setActiveTab("tracabilite")}>
                🔔 {alerts.length} alerte{alerts.length>1?"s":""}
              </span>
            )}
            {pendingSocietes.length > 0 && (
              <span style={{ ...S.pill, background:"#fef3c7", color:"#d97706", cursor:"pointer" }} onClick={() => setActiveTab("societes")}>
                ⏳ {pendingSocietes.length} inscription{pendingSocietes.length>1?"s":""}
              </span>
            )}
            {certStatus && (
              <span style={{ ...S.pill, ...(certOk ? { background:"#dcfce7", color:"#16a34a" } : { background:"#fee2e2", color:"#dc2626" }) }}>
                {certOk ? "🔒 Cert. valide" : "⚠️ Cert. expiré"}
              </span>
            )}
            <button style={S.btnSecondary} className="btn-hover" onClick={fetchAllData}>↻ Actualiser</button>
            <button style={S.btnPrimary} onClick={() => navigate("/facture/create")}>✏️ Nouvelle facture</button>
          </div>
        </header>

        {/* ══ BANNER: ASSIGNMENTS EN ATTENTE ══ */}
{assignments.filter(a => a.statut === "PENDING").length > 0 && activeTab !== "assignments" && (
  <div style={{
      background: "linear-gradient(135deg, #6c63ff, #4f46e5)",
      padding: "14px 32px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 12
    }}>
    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
      <div style={{ width:40, height:40, borderRadius:10, background:"rgba(255,255,255,0.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.3rem" }}>
        📬
      </div>
      <div>
        <div style={{ fontWeight:800, fontSize:"0.95rem", color:"#fff" }}>
          {assignments.filter(a => a.statut === "PENDING").length === 1
            ? "Vous avez une nouvelle société à gérer !"
            : `Vous avez ${assignments.filter(a => a.statut === "PENDING").length} nouvelles sociétés à gérer !`}
        </div>
        <div style={{ fontSize:"0.75rem", color:"rgba(255,255,255,0.8)", marginTop:2 }}>
          {assignments.filter(a => a.statut === "PENDING").map(a => a.societe_nom || `Société #${a.societe_id}`).join(", ")}
        </div>
      </div>
    </div>
    <button
      style={{
        padding:"10px 24px", borderRadius:10,
        border:"2px solid rgba(255,255,255,0.8)",
        background:"rgba(255,255,255,0.15)",
        cursor:"pointer", fontSize:"0.88rem",
        fontWeight:700, color:"#fff",
        fontFamily:"inherit"
      }}
      onClick={() => setActiveTab("assignments")}>
      📋 Voir mes demandes →
    </button>
  </div>
)}
        {/* ── TABS BAR ── */}
        <div style={S.tabsBar}>
          {TABS.map(t => (
            <button key={t.id}
              style={{ ...S.tabBtn, ...(activeTab===t.id ? S.tabBtnActive : {}) }}
              onClick={() => setActiveTab(t.id)}>
              <span>{t.icon}</span>
              <span>{t.label}</span>
              {t.id==="factures"    && factures.length>0         && <span style={S.tabCount}>{factures.length}</span>}
              {t.id==="tracabilite" && alerts.length>0            && <span style={{ ...S.tabCount, background:"#ef4444" }}>{alerts.length}</span>}
              {t.id==="societes"    && pendingSocietes.length>0   && <span style={{ ...S.tabCount, background:"#d97706" }}>{pendingSocietes.length}</span>}
            </button>
          ))}
        </div>

        <div style={S.content}>

          {/* ══════════════════════════════════════════════════
              TAB: VUE D'ENSEMBLE
          ══════════════════════════════════════════════════ */}
          {activeTab === "dashboard" && (
            <div style={S.tabContent}>
              {/* Hero (identique home.js) */}
              <div style={S.hero}>
                <div style={S.heroContent}>
                  <div style={S.heroTag}>LOGIMES · ADMIN</div>
                  <h2 style={S.heroTitle}>Bienvenue, <span style={{ color:"#6c63ff" }}>{currentUser}</span></h2>
                  <p style={S.heroDesc}>Supervisez les soumissions DSS, validez les inscriptions, gérez les sociétés et testez le sandbox ANCE depuis ce dashboard.</p>
                  <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
                    <button style={S.heroCta} onClick={() => setActiveTab("factures")}>🧾 Superviser DSS</button>
                    <button style={S.heroCtaSecondary} onClick={() => setActiveTab("societes")}>🏢 Gérer sociétés</button>
                  </div>
                </div>
                <div style={S.heroVisual}>
                  <div style={{ ...S.floatingCard, top:10, left:10, color:"#6c63ff", animationDelay:"0s" }}>📤 DSS soumis</div>
                  <div style={{ ...S.floatingCard, top:80, right:10, color:"#43b89c", animationDelay:"1s" }}>✅ {quickStats.accepted} acceptées</div>
                  <div style={{ ...S.floatingCard, bottom:10, left:40, color:"#d97706", animationDelay:"2s" }}>⏳ {pendingSocietes.length} inscriptions</div>
                </div>
              </div>

              {/* KPI Stats animés */}
              <div style={S.statsGrid}>
                {[
                  { icon:"📬", label:"Sociétés assignées", value: loading ? "..." : <AnimCount n={assignments.length} />, color:"#6c63ff", action: () => setActiveTab("assignments") },
                  { icon:"✅", label:"Demandes acceptées", value: loading ? "..." : <AnimCount n={assignments.filter(a=>a.statut==="ACCEPTED").length} />, color:"#43b89c", action: () => setActiveTab("assignments") },
                  { icon:"⏳", label:"En attente",          value: loading ? "..." : <AnimCount n={assignments.filter(a=>a.statut==="PENDING").length} />,  color:"#f59e0b", action: () => setActiveTab("assignments") },
                  { icon:"🏢", label:"Inscriptions",        value: loading ? "..." : <AnimCount n={pendingSocietes.length} />, color:"#e11d48", action: () => setActiveTab("societes") },                ].map((s, i) => (
                  <div key={i} style={S.statCard} className="stat-hover" onClick={s.action}>
                    <div style={S.statTop}><span style={S.statLabel}>{s.label}</span><div style={{ ...S.statDot, background:s.color }} /></div>
                    <div style={{ ...S.statValue, color:s.color }}>{s.value}</div>
                    <div style={S.statSub}>{s.icon} Cliquer pour voir</div>
                  </div>
                ))}
              </div>

{/* Rapport Admin → Super Admin */}
{assignments.filter(a => a.statut === "ACCEPTED").length > 0 && (
  <div style={{ background:"#fff", borderRadius:14, border:"1.5px solid #c7d2fe", padding:"20px 24px" }}>
    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
      <div style={{ width:36, height:36, borderRadius:9, background:"#f0efff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.1rem" }}>📤</div>
      <div>
        <div style={{ fontSize:"0.82rem", fontWeight:800, color:"#0F172A" }}>Notifier le Super Admin</div>
        <div style={{ fontSize:"0.7rem", color:"#94A3B8" }}>Envoyer un rapport sur vos sociétés gérées</div>
      </div>
    </div>
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
      {assignments.filter(a => a.statut === "ACCEPTED").map((a, i) => (
        <div key={i} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 16px", background:"#F8FAFC", borderRadius:10, border:"1px solid #e8e8f0" }}>
          <div>
            <div style={{ fontWeight:700, fontSize:"0.88rem", color:"#0F172A" }}>🏢 {a.societe_nom}</div>
            {a.admin_report
              ? <div style={{ fontSize:"0.72rem", color:"#16a34a", marginTop:3 }}>✅ Envoyé le: {a.reported_at ? new Date(a.reported_at).toLocaleDateString("fr-FR") : "—"}</div>
              : <div style={{ fontSize:"0.72rem", color:"#94A3B8", marginTop:3 }}>Aucun rapport envoyé</div>
            }
          </div>
          <button
            style={{ ...S.btnPrimary, padding:"8px 16px", fontSize:"0.8rem",
              background: a.admin_report ? "#16a34a" : "#6c63ff" }}
            onClick={async () => {
              const report = `Société ${a.societe_nom} — Workflow complété ✅ — ${new Date().toLocaleDateString("fr-FR")}`;
              try {
                const res = await fetch(
                  `${BACKEND_URL}/api/auth/assignments/${a.id}/report`,
                  { method:"PUT", headers:authH(), body:JSON.stringify({ admin_report: report }) }
                );
                const data = await res.json();
                if (data.success) { showToast(`✅ Rapport envoyé — ${a.societe_nom}`); fetchMyAssignments(); }
                else showToast(data.error || "Erreur", "error");
              } catch { showToast("Erreur réseau", "error"); }
            }}>
            {a.admin_report ? "✅ Déjà notifié" : "📤 Notifier Super Admin"}
          </button>
        </div>
      ))}
    </div>
  </div>
)}

              {/* Accès rapide */}
              <div style={S.sectionTitle}>⚡ Accès rapide</div>
              <div style={S.quickGrid}>
                {[
                  { icon:"✏️", label:"Créer Facture",   color:"#6c63ff", action:()=>navigate("/facture/create") },
                  { icon:"🧪", label:"Sandbox ANCE",    color:"#e11d48", action:()=>setActiveTab("sandbox")    },
                  { icon:"🏢", label:"Gérer Sociétés",  color:"#f59e0b", action:()=>setActiveTab("societes")   },
                  { icon:"👤", label:"Users Société",   color:"#9b59b6", action:()=>setActiveTab("users")      },
                ].map((q2, i) => (
                  <div key={i} style={{ ...S.quickCard, borderColor:q2.color+"30" }} className="stat-hover" onClick={q2.action}>
                    <span style={{ ...S.quickIcon, background:q2.color+"15", color:q2.color }}>{q2.icon}</span>
                    <span style={S.quickLabel}>{q2.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════
              TAB: SANDBOX ANCE (USE CASE 2 — Tester sandbox)
          ══════════════════════════════════════════════════ */}
          {activeTab === "sandbox" && (
            <div style={S.tabContent}>
              {sandboxRes && (
                <div style={S.statsGrid}>
                  {[
                    { label:"Total testées",  value:sandboxRes.total_tested??"—",         color:"#6c63ff", sub:"envoyées sandbox" },
                    { label:"Acceptées",       value:sandboxRes.summary?.ACCEPTED??"—",   color:"#43b89c", sub:"validées" },
                    { label:"Rejetées",        value:sandboxRes.summary?.REJECTED??"—",   color:"#e74c3c", sub:"rejetées" },
                    { label:"En attente",      value:sandboxRes.summary?.PENDING??"—",    color:"#f0a500", sub:"en cours" },
                  ].map((s,i)=><StatCard key={i} {...s} />)}
                </div>
              )}

              {/* USE CASE 2 — Tester sandbox ANCE */}
              <div style={{ background:"#fff",borderRadius:14,border:"1.5px solid #fecaca",padding:"20px 24px" }}>
                <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:14 }}>
                  <div style={{ width:36,height:36,borderRadius:9,background:"#fff0f0",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1.1rem" }}>🧪</div>
                  <div>
                    <div style={{ fontSize:"0.82rem",fontWeight:800,color:"#0F172A" }}>Tester Sandbox ANCE</div>
                    <div style={{ fontSize:"0.7rem",color:"#94A3B8" }}>Envoi de test sans impact fiscal réel — environnement bac à sable</div>
                  </div>
                </div>
                <div style={{ display:"flex",gap:10,alignItems:"center",flexWrap:"wrap" }}>
                  <input style={{ ...S.searchInput,border:"1.5px solid #E2E8F0",borderRadius:9,padding:"9px 14px",flex:1,minWidth:200,height:40 }}
                    type="text" placeholder="N° facture à tester..." value={sbNumFacture} onChange={e=>setSbNumFacture(e.target.value)} />
                  <select style={{ padding:"9px 14px",borderRadius:9,border:"1.5px solid #E2E8F0",fontSize:"0.82rem",fontFamily:"inherit",outline:"none",height:40 }}
                    value={sbEnv} onChange={e=>setSbEnv(e.target.value)}>
                    <option value="sandbox">🧪 Sandbox</option>
                    <option value="simulation">🔬 Simulation</option>
                  </select>
                  <button style={{ ...S.btnPrimary,background:"#e11d48" }} onClick={handleSandboxSubmit} disabled={sbLoading}>
                    {sbLoading?"⏳ Soumission...":"🧪 Envoyer au Sandbox"}
                  </button>
                </div>
                <p style={{ fontSize:"0.73rem",color:"#94A3B8",marginTop:10 }}>
                  ⚠️ Les tests sandbox n'ont aucun effet fiscal. Vous pouvez tester avec n'importe quelle facture.
                </p>
              </div>

              {/* Résultats sandbox */}
              <div style={S.sectionTitle}>📋 Derniers résultats sandbox</div>
              {!sandboxRes||sandboxRes.invoices?.length===0 ? (
                <EmptyState icon="🧪" text="Aucun test sandbox effectué" />
              ) : (
                <div style={S.tableCard}>
                  <table style={S.table}>
                    <thead><tr>{["#","Facture","UUID fiscal","Statut","Environnement","Date","Actions"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {(sandboxRes.invoices||[]).map((inv,i)=>(
                        <tr key={i} style={S.tr} onMouseEnter={e=>e.currentTarget.style.background="#FAFBFF"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                          <td style={S.td}><span style={S.idNum}>{i+1}</span></td>
                          <td style={S.td}><span style={{ fontWeight:700,color:"#6c63ff" }}>{inv.invoice_number||inv.num||"—"}</span></td>
                          <td style={S.td}><span style={{ fontSize:"0.7rem",color:"#94A3B8",fontFamily:"monospace" }}>{inv.uuid_dss||inv.uuid||"—"}</span></td>
                          <td style={S.td}><StatusBadge status={inv.status} /></td>
                          <td style={S.td}>
                            <span style={{ display:"inline-flex",alignItems:"center",gap:4,padding:"2px 8px",borderRadius:20,fontSize:"0.7rem",fontWeight:700, background:inv.environment==="sandbox"?"#fff0f0":"#f0f9ff",color:inv.environment==="sandbox"?"#e11d48":"#0891b2" }}>
                              {inv.environment==="sandbox"?"🧪 Sandbox":"🔬 Simulation"} {inv.environment||"—"}
                            </span>
                          </td>
                          <td style={S.td}><span style={S.dateCell}>{inv.created_at?new Date(inv.created_at).toLocaleDateString("fr-FR"):"—"}</span></td>
                          <td style={S.td}>
                            <button style={{ ...S.actionBtn,background:"#f0efff",color:"#6c63ff",border:"1px solid #c7d2fe",fontSize:"0.7rem" }}
                              onClick={()=>{ setSbNumFacture(String(inv.invoice_number||"")); }}>
                              ↺ Re-tester
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════════════
              TAB: USERS
          ══════════════════════════════════════════════════ */}
{activeTab === "users" && (
  <div style={S.tabContent}>
    <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}>
      <div style={S.sectionTitle}>👤 Utilisateurs de ma société</div>
      <button style={S.btnSecondary} onClick={fetchCompanyUsers}>↻ Actualiser</button>
    </div>
    {usersLoading ? <StateBox loading /> : companyUsers.length===0 ? <EmptyState icon="👤" text="Aucun utilisateur trouvé" /> : (
      <div style={S.tableCard}>
        <table style={S.table}>
          {/* ✅ تعديل 1: زدين "Actions" في الـ header */}
          <thead><tr>{["#","Nom","Email","Rôle","Société","Statut","Créé le", "Actions"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {companyUsers.filter(u => u.role !== 'super_admin').map((u,i)=>(
              <tr key={u.id||i} style={S.tr} onMouseEnter={e=>e.currentTarget.style.background="#FAFBFF"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                <td style={S.td}><span style={S.idNum}>{i+1}</span></td>
                <td style={S.td}>
                  <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                    <div style={{ width:32,height:32,borderRadius:8,background:"#9b59b6",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"0.75rem",fontWeight:800 }}>
                      {(u.name||u.nom||"?").charAt(0).toUpperCase()}
                    </div>
                    <span style={{ fontSize:"0.85rem",fontWeight:700,color:"#0F172A" }}>{u.name||u.nom||"—"}</span>
                  </div>
                </td>
                <td style={S.td}><span style={S.emailCell}>{u.email||"—"}</span></td>
                <td style={S.td}>
                  <span style={{ fontSize:"0.75rem",fontWeight:700,color:u.role==="admin"?"#6c63ff":"#64748B",background:u.role==="admin"?"#f0efff":"#F1F5F9",padding:"2px 8px",borderRadius:10 }}>
                    {u.role||"user"}
                  </span>
                </td>
                <td style={S.td}>
                  <span style={{ fontSize:"0.75rem",fontWeight:700,color:u.is_active?"#16a34a":"#dc2626",background:u.is_active?"#dcfce7":"#fee2e2",padding:"2px 8px",borderRadius:10 }}>
                    {u.is_active?"✓ Actif":"✗ Inactif"}
                  </span>
                </td>
                <td style={S.td}>
                  <span style={{ fontSize:"0.75rem", color:"#6c63ff", fontWeight:600 }}>
                    {u.societe_id || "—"}
                  </span>
                </td>
                <td style={S.td}><span style={S.dateCell}>{u.created_at?new Date(u.created_at).toLocaleDateString("fr-FR"):"—"}</span></td>
                
                {/* ✅ تعديل 2: زدين الـ column الجديد "Modifier" */}
                <td style={{ ...S.td, textAlign: "right" }}>
                  <button
                    style={{ ...S.actionBtn, color: "#f59e0b", borderColor: "#f59e0b", background: "#fffbeb" }}
                    onClick={() => openModifierModal(u)}
                    title="Demander une modification"
                  >
                    ✏️ Modifier
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
)}


{/* ══ TAB: DEMANDES DE MODIFICATION ══ */}
{activeTab === "modifications" && (
  <div style={S.tabContent}>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
      <div style={S.sectionTitle}>✏️ Demandes de modification en attente</div>
      <button style={S.btnSecondary} onClick={fetchModificationRequests} disabled={modReqLoading}>↻ Actualiser</button>
    </div>

    {modReqLoading ? <StateBox loading /> : modRequests.length === 0 ? (
      <EmptyState icon="✅" text="Aucune demande en attente" />
    ) : (
      <div style={S.tableCard}>
        <table style={S.table}>
          <thead>
            <tr>{["Utilisateur", "Champ", "Ancienne valeur", "Nouvelle valeur", "Demandé par", "Date", "Actions"].map(h => (
              <th key={h} style={S.th}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {modRequests.map(req => (
              <tr key={req.id} style={S.tr}
                onMouseEnter={e => e.currentTarget.style.background = "#FAFBFF"}
                onMouseLeave={e => e.currentTarget.style.background = ""}>
                
                <td style={S.td}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <div style={{ width:32, height:32, borderRadius:8, background:"#7C3AED18", color:"#7C3AED", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.75rem", fontWeight:800 }}>
                      {(req.user_name||"?").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize:"0.85rem", fontWeight:700, color:"#0F172A" }}>{req.user_name}</div>
                      <div style={{ fontSize:"0.7rem", color:"#94A3B8" }}>{req.user_email}</div>
                    </div>
                  </div>
                </td>

                <td style={S.td}>
                  <span style={{ fontSize:"0.75rem", fontWeight:700, color:"#6c63ff", background:"#f0efff", padding:"2px 8px", borderRadius:10 }}>
                    {req.field_name === 'name' ? '👤 Nom' : req.field_name === 'email' ? '📧 Email' : '🔑 Rôle'}
                  </span>
                </td>

                <td style={S.td}><del style={{ color:"#94A3B8", fontSize:"0.82rem" }}>{req.old_value||"—"}</del></td>
                <td style={S.td}><strong style={{ color:"#16a34a", fontSize:"0.85rem" }}>{req.new_value}</strong></td>

                <td style={S.td}>
                  <div style={{ fontSize:"0.82rem", fontWeight:600, color:"#0F172A" }}>{req.requested_by_name}</div>
                  <div style={{ fontSize:"0.7rem", color:"#94A3B8" }}>{req.requested_by_email}</div>
                </td>

                <td style={S.td}><span style={S.dateCell}>{new Date(req.created_at).toLocaleDateString('fr-FR')}</span></td>

                <td style={S.td}>
                  <div style={{ display:"flex", gap:6 }}>
                    <button
                      style={{ ...S.actionBtn, color:"#16a34a", borderColor:"#16a34a", background:"#f0fdf4", fontWeight:700 }}
                      onClick={() => handleModRequestAction(req.id, "approve")}
                      disabled={modActionLoading[req.id]}
                    >
                      {modActionLoading[req.id] ? "⏳..." : "✅ Accepter"}
                    </button>
                    <button
                      style={{ ...S.actionBtn, color:"#dc2626", borderColor:"#dc2626", background:"#fff0f0", fontWeight:700 }}
                      onClick={() => {
                        const reason = prompt("Raison du rejet (optionnel) :");
                        handleModRequestAction(req.id, "reject", reason || "");
                      }}
                      disabled={modActionLoading[req.id]}
                    >
                      {modActionLoading[req.id] ? "⏳..." : "❌ Refuser"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
)}

{/* ══ TAB: MES DEMANDES ══ */}
{activeTab === "assignments" && (
  <div style={S.tabContent}>

    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
      <div style={S.sectionTitle}>📬 Mes Demandes de Gestion</div>
      <button style={S.btnSecondary} onClick={fetchMyAssignments}>↻ Actualiser</button>
    </div>

    {assignLoading ? <StateBox loading /> : assignments.length === 0 ? (
      <div style={S.emptyState}>
        <div style={{ fontSize:"2.5rem" }}>📭</div>
        <p style={{ fontSize:"0.84rem", color:"#94A3B8", fontWeight:600 }}>Aucune demande reçue</p>
        <p style={{ fontSize:"0.75rem", color:"#CBD5E1", textAlign:"center" }}>
          Le Super Admin vous assignera des sociétés à gérer
        </p>
      </div>
    ) : (
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        {assignments.map((a, i) => (
          <div key={i} style={{
            background:"#fff", borderRadius:14,
            border:`2px solid ${a.statut==="ACCEPTED"?"#bbf7d0":a.statut==="PENDING"?"#fde68a":"#e8e8f0"}`,
            padding:"22px", display:"flex", flexDirection:"column", gap:14
          }}>
            {/* Header */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:10 }}>
              <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                <div style={{ width:50, height:50, borderRadius:13,
                  background: a.statut==="ACCEPTED"?"#dcfce7":"#fef3c7",
                  display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.4rem" }}>
                  {a.statut==="ACCEPTED" ? "✅" : "⏳"}
                </div>
                <div>
                  <div style={{ fontWeight:800, fontSize:"1rem", color:"#0F172A" }}>
                    {a.societe_nom || `Société #${a.societe_id}`}
                  </div>
                  <div style={{ fontSize:"0.72rem", color:"#94A3B8", marginTop:4 }}>
                    📅 Reçue le {a.created_at ? new Date(a.created_at).toLocaleDateString("fr-FR") : "—"}
                  </div>
                </div>
              </div>
              <span style={{
                fontSize:"0.78rem", fontWeight:700, padding:"6px 16px", borderRadius:20,
                background: a.statut==="ACCEPTED"?"#dcfce7":"#fef3c7",
                color: a.statut==="ACCEPTED"?"#16a34a":"#d97706",
                border:`1px solid ${a.statut==="ACCEPTED"?"#bbf7d0":"#fde68a"}`
              }}>
                {a.statut==="ACCEPTED" ? "✅ Acceptée" : "⏳ En attente"}
              </span>
            </div>

            {/* Commentaire Super Admin */}
            {a.commentaire && (
              <div style={{ background:"#F8FAFC", borderRadius:10, padding:"14px 18px",
                borderLeft:"4px solid #6c63ff" }}>
                <div style={{ fontSize:"0.65rem", fontWeight:700, color:"#94A3B8",
                  textTransform:"uppercase", marginBottom:6 }}>
                  💬 Instructions du Super Admin
                </div>
                <div style={{ fontSize:"0.85rem", color:"#334155", lineHeight:1.6 }}>
                  {a.commentaire}
                </div>
              </div>
            )}

            {/* Actions */}
            <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
              {a.statut === "PENDING" && (
                <button
                  style={{ ...S.btnPrimary, background:"#16a34a", padding:"10px 20px" }}
                  onClick={async () => {
                    try {
                      const res = await fetch(
                        `${BACKEND_URL}/api/auth/assignments/${a.id}/accept`,
                        { method:"PUT", headers:authH() }
                      );
                      const data = await res.json();
                      if (data.success) {
                        showToast(`✅ Demande acceptée — ${a.societe_nom}`);
                        addNotification({
                          type:"success",
                          title:"Demande acceptée",
                          message:`Société ${a.societe_nom} — vous pouvez maintenant la gérer`
                        });
                        fetchMyAssignments();
                      } else {
                        showToast(data.error || "Erreur", "error");
                      }
                    } catch { showToast("Erreur réseau", "error"); }
                  }}>
                  ✅ Accepter la demande
                </button>
              )}
                {a.statut === "ACCEPTED" && (
                  <button
                    style={{ ...S.btnPrimary, padding:"10px 20px" }}
onClick={() => {
  // ✅ societe_id = numeri (73) moch KARMEX
  document.cookie = `societe_id=${a.societe_id};path=/;max-age=86400;SameSite=Lax`;
  document.cookie = `societe_folder=${a.societe_folder || a.societe_nom};path=/;max-age=86400;SameSite=Lax`;
  document.cookie = `societe=${a.societe_nom || "Société"};path=/;max-age=86400;SameSite=Lax`;
  document.cookie = `name=${getCookie("name")};path=/;max-age=86400;SameSite=Lax`;
  document.cookie = `role=admin;path=/;max-age=86400;SameSite=Lax`;
  document.cookie = `token=${getCookie("token")};path=/;max-age=86400;SameSite=Lax`;
  
  setTimeout(() => { window.location.href = "/home"; }, 300);
}}>
                        🧾 Gérer les factures
                  </button>
                )}
              {a.statut === "ACCEPTED" && (
                <button
                  style={{ ...S.btnSecondary, padding:"10px 20px" }}
                  onClick={() => setActiveTab("clients")}>
                  👥 Voir les clients
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
)}    

          {/* ══════════════════════════════════════════════════
              TAB: SOCIÉTÉS
              USE CASE 3 — Modifier infos sensibles société
              USE CASE 4 — Valider inscription + CRUD
          ══════════════════════════════════════════════════ */}
          {activeTab === "societes" && (
            <div style={S.tabContent}>

              {/* USE CASE 4 — Valider inscription société */}
              <div style={{ background:"#fff",borderRadius:14,border:"1.5px solid #fde68a",padding:"20px 24px" }}>
                <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16 }}>
                  <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                    <div style={{ width:36,height:36,borderRadius:9,background:"#fef3c7",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1.1rem" }}>⏳</div>
                    <div>
                      <div style={{ fontSize:"0.82rem",fontWeight:800,color:"#0F172A" }}>Inscriptions en attente ({pendingSocietes.length})</div>
                      <div style={{ fontSize:"0.7rem",color:"#94A3B8" }}>Valider ou rejeter les nouvelles demandes d'inscription</div>
                    </div>
                  </div>
                  <button style={S.btnSecondary} onClick={fetchSocietes}>↻</button>
                </div>

                {socLoading ? <StateBox loading /> : pendingSocietes.length===0 ? (
                  <div style={{ display:"flex",alignItems:"center",gap:12,padding:"16px",background:"#f0fdf4",borderRadius:10,border:"1px solid #bbf7d0" }}>
                    <span style={{ fontSize:"1.2rem" }}>✅</span>
                    <p style={{ fontSize:"0.82rem",color:"#16a34a",fontWeight:600 }}>Aucune inscription en attente de validation</p>
                  </div>
                ) : (
                  <div style={S.tableCard}>
                    <table style={S.table}>
                      <thead><tr>{["#","Nom","Email","Date inscription","Actions"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                      <tbody>
                        {pendingSocietes.map((u,i)=>(
                          <tr key={u.id||i} style={S.tr} onMouseEnter={e=>e.currentTarget.style.background="#FAFBFF"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                            <td style={S.td}><span style={S.idNum}>{i+1}</span></td>
                            <td style={S.td}>
                              <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                                <div style={{ width:32,height:32,borderRadius:"50%",background:"#fef3c7",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"0.85rem",fontWeight:800,color:"#d97706" }}>
                                  {(u.name||u.nom||"?").charAt(0).toUpperCase()}
                                </div>
                                <span style={{ fontWeight:700,fontSize:"0.85rem" }}>{u.name||u.nom||"—"}</span>
                              </div>
                            </td>
                            <td style={S.td}><span style={S.emailCell}>{u.email||"—"}</span></td>
                            <td style={S.td}><span style={S.dateCell}>{u.created_at?new Date(u.created_at).toLocaleDateString("fr-FR"):"—"}</span></td>
                            <td style={S.td}>
                              <div style={{ display:"flex",gap:6 }}>
                                <button style={{ ...S.actionBtn,color:"#16a34a",borderColor:"#16a34a",fontWeight:700,background:"#f0fdf4" }}
                                  onClick={()=>handleActivate(u.id)} disabled={activateLoading[u.id]}>
                                  {activateLoading[u.id]?"⏳...":"✓ Valider"}
                                </button>
                                <button style={{ ...S.actionBtn,color:"#dc2626",borderColor:"#dc2626",background:"#fff0f0" }}
                                  onClick={()=>handleRejectActivation(u.id)}>
                                  ✕ Rejeter
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════
              TAB: PARAMÈTRES
          ══════════════════════════════════════════════════ */}
{activeTab === "parametres" && (
  <div style={S.tabContent}>

    {/* ── Config Serveurs ── */}
    <div style={{ background:"#fff", borderRadius:14, border:"1.5px solid #c7d2fe", padding:"20px 24px" }}>
      <div style={S.sectionTitle}>🔌 Configuration Serveurs</div>
      <div style={{ display:"flex", flexDirection:"column", gap:14, marginTop:14 }}>
        {[
          { label:"Middleware URL", key:"MIDDLEWARE_URL", default:process.env.REACT_APP_API_URL         || "", icon:"⚙️" },
          { label:"Backend URL",   key:"BACKEND_URL",    default:process.env.REACT_APP_BACKEND_URL     || "", icon:"🔐" },
          { label:"Bridge URL",    key:"BRIDGE_URL",     default:process.env.REACT_APP_BRIDGE_URL      || "", icon:"🌉" },
        ].map(f => (
          <div key={f.key} style={{ display:"flex", flexDirection:"column", gap:6 }}>
            <label style={{ fontSize:"0.72rem", fontWeight:700, color:"#64748B", textTransform:"uppercase" }}>
              {f.icon} {f.label}
            </label>
            <div style={{ display:"flex", gap:8 }}>
              <input id={`cfg_${f.key}`}
                style={{ flex:1, padding:"10px 14px", border:"1.5px solid #E2E8F0", borderRadius:9, fontSize:"0.82rem", fontFamily:"monospace", outline:"none" }}
                defaultValue={sysConfig[f.key] || f.default}
              />
              <button style={{ ...S.btnPrimary, padding:"10px 18px" }}
                onClick={async () => {
                  const val = document.getElementById(`cfg_${f.key}`).value;
                  const res = await fetch(`${BACKEND_URL}/api/auth/config/${f.key}`, {
                    method:"PUT", headers:authH(), body:JSON.stringify({ value: val })
                  });
                  const data = await res.json();
                  if (data.success) { showToast(`✅ ${f.label} sauvegardé !`); fetchSysConfig(); }
                  else showToast("Erreur", "error");
                }}>
                💾 Sauvegarder
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* ── Config DB ── */}
    <div style={{ background:"#fff", borderRadius:14, border:"1.5px solid #fde68a", padding:"20px 24px" }}>
      <div style={S.sectionTitle}>🗄️ Base de Données PostgreSQL</div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginTop:14 }}>
        {[
          { label:"DB Host",     key:"DB_HOST",     default:"localhost", icon:"🖥️" },
          { label:"DB Port",     key:"DB_PORT",     default:"5432",      icon:"🔌" },
          { label:"DB Name",     key:"DB_NAME",     default:"pfe_db",    icon:"📦" },
          { label:"DB User",     key:"DB_USER",     default:"postgres",  icon:"👤" },
          { label:"DB Password", key:"DB_PASSWORD", default:"",          icon:"🔐" },
        ].map(f => (
          <div key={f.key} style={{ display:"flex", flexDirection:"column", gap:6 }}>
            <label style={{ fontSize:"0.72rem", fontWeight:700, color:"#64748B", textTransform:"uppercase" }}>
              {f.icon} {f.label}
            </label>
            <div style={{ display:"flex", gap:8 }}>
              <input id={`cfg_${f.key}`}
                type={f.key.includes("PASSWORD") ? "password" : "text"}
                style={{ flex:1, padding:"10px 14px", border:"1.5px solid #E2E8F0", borderRadius:9, fontSize:"0.82rem", fontFamily:"monospace", outline:"none" }}
                defaultValue={sysConfig[f.key] || f.default}
              />
              <button style={{ ...S.btnPrimary, background:"#d97706", padding:"10px 14px" }}
                onClick={async () => {
                  const val = document.getElementById(`cfg_${f.key}`).value;
                  const res = await fetch(`${BACKEND_URL}/api/auth/config/${f.key}`, {
                    method:"PUT", headers:authH(), body:JSON.stringify({ value: val })
                  });
                  const data = await res.json();
                  if (data.success) { showToast(`✅ ${f.label} sauvegardé !`); fetchSysConfig(); }
                  else showToast("Erreur", "error");
                }}>
                💾
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* ── Config Email ── */}
    <div style={{ background:"#fff", borderRadius:14, border:"1.5px solid #bbf7d0", padding:"20px 24px" }}>
      <div style={S.sectionTitle}>📧 Configuration Email</div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginTop:14 }}>
        {[
          { label:"Email SMTP",   key:"EMAIL_USER", default:"facturation.pfe2026@gmail.com", icon:"📧" },
          { label:"App Password", key:"EMAIL_PASS", default:"", icon:"🔐" },
        ].map(f => (
          <div key={f.key} style={{ display:"flex", flexDirection:"column", gap:6 }}>
            <label style={{ fontSize:"0.72rem", fontWeight:700, color:"#64748B", textTransform:"uppercase" }}>
              {f.icon} {f.label}
            </label>
            <div style={{ display:"flex", gap:8 }}>
              <input id={`cfg_${f.key}`}
                type={f.key.includes("PASS") ? "password" : "email"}
                style={{ flex:1, padding:"10px 14px", border:"1.5px solid #E2E8F0", borderRadius:9, fontSize:"0.82rem", fontFamily:"monospace", outline:"none" }}
                defaultValue={sysConfig[f.key] || f.default}
              />
              <button style={{ ...S.btnPrimary, background:"#16a34a", padding:"10px 14px" }}
                onClick={async () => {
                  const val = document.getElementById(`cfg_${f.key}`).value;
                  const res = await fetch(`${BACKEND_URL}/api/auth/config/${f.key}`, {
                    method:"PUT", headers:authH(), body:JSON.stringify({ value: val })
                  });
                  const data = await res.json();
                  if (data.success) { showToast(`✅ ${f.label} sauvegardé !`); fetchSysConfig(); }
                  else showToast("Erreur", "error");
                }}>
                💾
              </button>
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop:10, padding:"10px 14px", background:"#fffbeb", borderRadius:8, border:"1px solid #fde68a", fontSize:"0.75rem", color:"#92400e" }}>
        ⚠️ Après modification — redémarrez le backend pour appliquer les changements.
      </div>
    </div>

    {/* ── Certificat ── */}
    <div style={S.sectionTitle}>🔒 Certificat ANCE</div>
    <div style={S.statsGrid}>
      {[
        { label:"Certificat", value:loading?"...":(certOk?"✓ Valide":"✗ Expiré"), color:certOk?"#43b89c":"#e74c3c", sub:certStatus?.certificate_info?.expires_at||"—" },
        { label:"Backend",    value:"✓ Online", color:"#43b89c", sub:"Port 5000"       },
        { label:"Middleware", value:"✓ Online", color:"#43b89c", sub:"Port 8443 HTTPS" },
        { label:"PostgreSQL", value:"✓ Active", color:"#43b89c", sub:"pfe_db"          },
      ].map((s,i)=><StatCard key={i} {...s} />)}
    </div>

    {/* ── SSH Config ── */}
    <div style={{ background:"#fff", borderRadius:14, border:"1.5px solid #f59e0b40", padding:"20px 24px" }}>
      <div style={S.sectionTitle}>🔑 Configuration SSH Bridge</div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginTop:14 }}>
        {[
          { label:"SSH Host",     key:"SSH_HOST", default:"10.10.10.25", icon:"🖥️" },
          { label:"SSH Port",     key:"SSH_PORT", default:"22",          icon:"🔌" },
          { label:"SSH User",     key:"SSH_USER", default:"logimes01",   icon:"👤" },
          { label:"SSH Password", key:"SSH_PASS", default:"",            icon:"🔐" },
        ].map(f => (
          <div key={f.key} style={{ display:"flex", flexDirection:"column", gap:6 }}>
            <label style={{ fontSize:"0.72rem", fontWeight:700, color:"#64748B", textTransform:"uppercase" }}>
              {f.icon} {f.label}
            </label>
            <div style={{ display:"flex", gap:8 }}>
              <input id={`cfg_${f.key}`}
                type={f.key.includes("PASS") ? "password" : "text"}
                style={{ flex:1, padding:"10px 14px", border:"1.5px solid #E2E8F0", borderRadius:9, fontSize:"0.82rem", fontFamily:"monospace", outline:"none" }}
                defaultValue={sysConfig[f.key] || f.default}
              />
              <button style={{ ...S.btnPrimary, background:"#f59e0b", padding:"10px 14px" }}
                onClick={async () => {
                  const val = document.getElementById(`cfg_${f.key}`).value;
                  const res = await fetch(`${BACKEND_URL}/api/auth/config/${f.key}`, {
                    method:"PUT", headers:authH(), body:JSON.stringify({ value: val })
                  });
                  const data = await res.json();
                  if (data.success) { showToast(`✅ ${f.label} sauvegardé !`); fetchSysConfig(); }
                  else showToast("Erreur", "error");
                }}>
                💾
              </button>
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop:10, padding:"10px 14px", background:"#fffbeb", borderRadius:8, border:"1px solid #fde68a", fontSize:"0.75rem", color:"#92400e" }}>
        ⚠️ Après modification — redémarrez le backend pour appliquer les changements SSH.
      </div>
    </div>

    {/* ── SSH Bridge Restart ── */}
<div style={{ background:"#fff", borderRadius:14, border:"1.5px solid #fde68a", padding:"20px 24px" }}>
  <div style={S.sectionTitle}>🔄 Gestion Système</div>
  
  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", 
    padding:"16px", background:"#F8FAFC", borderRadius:10, border:"1px solid #e8e8f0" }}>
    <div>
      <div style={{ fontWeight:700, fontSize:"0.92rem", color:"#0F172A", marginBottom:4 }}>
        🌉 Bridge Paradox
      </div>
      <div style={{ fontSize:"0.75rem", color:"#94A3B8" }}>
        10.10.10.25 · SSH · Python Bridge
      </div>
    </div>
    <button
      style={{ ...S.btnPrimary, background:"#f59e0b", padding:"10px 20px" }}
      onClick={async () => {
        showToast("⏳ Redémarrage Bridge en cours...");
        try {
          const res = await fetch(
            `${BACKEND_URL}/api/system/bridge/restart-remote`,
            { method:"POST", headers:authH() }
          );
          const data = await res.json();
          if (data.success) showToast("✅ Bridge redémarré !");
          else showToast(`❌ ${data.message}`, "error");
        } catch { showToast("❌ Erreur connexion", "error"); }
      }}>
      🔄 Restart Bridge
    </button>
  </div>

  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
    padding:"16px", background:"#F8FAFC", borderRadius:10, border:"1px solid #e8e8f0", marginTop:10 }}>
    <div>
      <div style={{ fontWeight:700, fontSize:"0.92rem", color:"#0F172A", marginBottom:4 }}>
        🔐 Backend Express
      </div>
      <div style={{ fontSize:"0.75rem", color:"#94A3B8" }}>
        das-cloud.com:5000 · Node.js · PM2
      </div>
    </div>
    <button
      style={{ ...S.btnPrimary, background:"#6c63ff", padding:"10px 20px" }}
      onClick={async () => {
        showToast("⏳ Redémarrage backend...");
        try {
          const res = await fetch(
            `${BACKEND_URL}/api/system/backend/restart`,
            { method:"POST", headers:authH() }
          );
          const data = await res.json();
          if (data.success) showToast("✅ Backend redémarré !");
          else showToast(`❌ ${data.message}`, "error");
        } catch { showToast("🔄 Backend redémarre..."); }
      }}>
      🔄 Restart Backend
    </button>
  </div>

</div>

  </div>
)}

</div>
</main>

      {/* ════════ MODAL DÉTAIL FACTURE ════════ */}
      {detailModal && (
        <div style={S.modalOverlay} onClick={()=>setDetailModal(null)}>
          <div style={{ ...S.modal,maxWidth:560 }} onClick={e=>e.stopPropagation()}>
            <div style={S.modalHeader}>
              <div>
                <div style={{ fontSize:"0.68rem",fontWeight:700,color:"#6c63ff",textTransform:"uppercase",marginBottom:3 }}>Facture électronique</div>
                <h3 style={{ fontSize:"1rem",fontWeight:800,color:"#0F172A" }}>#{detailModal.NumfactAffich||detailModal.NumFacture}</h3>
              </div>
              <button style={S.modalClose} onClick={()=>setDetailModal(null)}>✕</button>
            </div>
            <div style={S.modalBody}>
              {(() => { const st=getStatut(getStatutField(detailModal)); return <span style={{ ...S.badge,color:st.color,background:st.bg,marginBottom:16,fontSize:"0.82rem",padding:"6px 14px",display:"inline-flex" }}>{st.icon} {st.label}</span>; })()}
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:20 }}>
                {[
                  ["N° Facture",  detailModal.NumfactAffich||detailModal.NumFacture],
                  ["N° Comptable",detailModal.NumFactureComptable],
                  ["Client",      detailModal.NomClient||detailModal.CodeClient],
                  ["Date",        detailModal.DateFacture?new Date(detailModal.DateFacture).toLocaleDateString("fr-FR"):"—"],
                  ["Montant HT",  detailModal.MontantTotalHT!=null?Number(detailModal.MontantTotalHT).toFixed(3)+" "+getDevise(detailModal):"—"],
                  ["TVA",         detailModal.MontantTVA1!=null?Number(detailModal.MontantTVA1).toFixed(3)+" "+getDevise(detailModal):"—"],
                  ["Montant TTC", detailModal.MontantTotalTTC!=null?Number(detailModal.MontantTotalTTC).toFixed(3)+" "+getDevise(detailModal):"—"],
                ].map(([k,v])=>(
                  <div key={k} style={{ background:"#F8FAFC",borderRadius:8,padding:"10px 14px" }}>
                    <span style={{ display:"block",fontSize:"0.62rem",color:"#94A3B8",fontWeight:700,textTransform:"uppercase",marginBottom:2 }}>{k}</span>
                    <span style={{ display:"block",fontSize:"0.86rem",fontWeight:700,color:"#0F172A" }}>{v||"—"}</span>
                  </div>
                ))}
              </div>
              {/* Workflow */}
              <div style={{ background:"#F8FAFC",borderRadius:10,padding:"12px 14px" }}>
                <div style={{ fontSize:"0.62rem",fontWeight:700,color:"#94A3B8",textTransform:"uppercase",marginBottom:10 }}>Parcours TEIF</div>
                <div style={{ display:"flex",alignItems:"flex-start",justifyContent:"space-between" }}>
                  {WORKFLOW_STEPS.map((step,si)=>{
                    const stepIdx=getWorkflowStep(getStatutField(detailModal)),done=si<=stepIdx,cur=si===stepIdx;
                    return (
                      <div key={si} style={{ display:"flex",flexDirection:"column",alignItems:"center",flex:1,position:"relative" }}>
                        {si>0 && <div style={{ position:"absolute",top:14,right:"50%",width:"100%",height:2,background:si<=stepIdx?step.color:"#e8e8f0",zIndex:0 }} />}
                        <div style={{ width:cur?30:24,height:cur?30:24,borderRadius:"50%",background:done?step.color:"#e8e8f0",display:"flex",alignItems:"center",justifyContent:"center",position:"relative",zIndex:1,marginBottom:5,boxShadow:cur?`0 0 0 3px ${step.color}35`:"none" }}>
                          <span style={{ fontSize:"0.6rem" }}>{step.icon}</span>
                        </div>
                        <div style={{ fontSize:"0.55rem",fontWeight:cur?700:400,color:done?step.color:"#94A3B8",textAlign:"center" }}>{step.label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div style={S.modalFooter}>
              <button style={S.btnSecondary} onClick={()=>setDetailModal(null)}>Fermer</button>
              <button style={{ ...S.actionBtn,background:"#f0f9ff",color:"#0891b2",border:"1px solid #bae6fd",padding:"9px 16px" }} onClick={()=>downloadXML(detailModal.NumFacture)} disabled={xmlLoading[detailModal.NumFacture]}>
                {xmlLoading[detailModal.NumFacture]?"⏳":"⬇️ XML"}
              </button>
              <button style={{ ...S.btnSecondary,background:"#fff0f0",color:"#dc2626",border:"1px solid #fecaca" }} onClick={()=>{downloadPDF(detailModal.NumfactAffich||detailModal.NumFacture);setDetailModal(null);}}>
                📄 PDF
              </button>
              <button style={{ ...S.btnSecondary,background:"#f0f9ff",color:"#0891b2",border:"1px solid #bae6fd" }} onClick={()=>{setDetailModal(null);fetchTrace(detailModal.NumFacture);}}>
                🔍 Traçabilité
              </button>
              {getStatutField(detailModal)==="REJECTED" && (
                <button style={{ ...S.btnSecondary,background:"#fff7ed",color:"#ea580c",border:"1px solid #fed7aa",fontWeight:700 }} onClick={()=>{handleRetry(detailModal.NumFacture);setDetailModal(null);}}>
                  🔄 Retry ANCE
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* ════════ MODAL: DEMANDER MODIFICATION (Admin) ════════ */}
{modModalOpen && modTargetUser && (
  <div style={S.modalOverlay} onClick={() => setModModalOpen(false)}>
    <div style={{ ...S.modal, maxWidth: 480 }} onClick={e => e.stopPropagation()}>
      <div style={S.modalHeader}>
        <div>
          <div style={{ fontSize:"0.68rem", fontWeight:700, color:"#f59e0b", textTransform:"uppercase", marginBottom:3 }}>
            Demande de modification
          </div>
          <h3 style={{ fontSize:"1rem", fontWeight:800, color:"#0F172A" }}>
            {modTargetUser.name}
          </h3>
        </div>
        <button style={S.modalClose} onClick={() => setModModalOpen(false)}>✕</button>
      </div>
      
      <div style={S.modalBody}>
        <div style={{ padding:"12px 16px", background:"#fffbeb", borderRadius:9, border:"1px solid #fde68a", fontSize:"0.78rem", color:"#92400e", marginBottom:16 }}>
          ⚠️ Cette demande sera envoyée au <strong>Super Admin</strong> pour validation.
        </div>
        
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
            <label style={{ fontSize:"0.72rem", fontWeight:700, color:"#64748B" }}>Champ à modifier</label>
            <select 
              style={{ padding:"10px 14px", borderRadius:9, border:"1.5px solid #E2E8F0", fontSize:"0.84rem", fontFamily:"inherit", outline:"none" }}
              value={modForm.field}
              onChange={(e) => {
                const field = e.target.value;
                setModForm({ field, newValue: modTargetUser[field] || '' });
              }}
            >
              <option value="name">Nom complet</option>
              <option value="email">Email</option>
              <option value="role">Rôle</option>
            </select>
          </div>
          
          <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
            <label style={{ fontSize:"0.72rem", fontWeight:700, color:"#64748B" }}>Nouvelle valeur</label>
            <input 
              style={{ padding:"10px 14px", borderRadius:9, border:"1.5px solid #E2E8F0", fontSize:"0.84rem", fontFamily:"inherit", outline:"none" }}
              type="text"
              placeholder={`Nouveau ${modForm.field === 'name' ? 'nom' : modForm.field === 'email' ? 'email' : 'rôle'}`}
              value={modForm.newValue}
              onChange={(e) => setModForm(prev => ({ ...prev, newValue: e.target.value }))}
            />
          </div>
        </div>
      </div>
      
      <div style={S.modalFooter}>
        <button style={S.btnSecondary} onClick={() => setModModalOpen(false)}>Annuler</button>
        <button 
          style={{ ...S.btnPrimary, background:"#f59e0b" }}
          onClick={sendModificationRequest}
          disabled={modLoading}
        >
          {modLoading ? "⏳ Envoi..." : "📤 Envoyer la demande"}
        </button>
      </div>
    </div>
  </div>
)}

      {/* ════════ TOAST ════════ */}
      {toast && (
        <div style={{ ...S.toast,...(toast.type==="error"?S.toastErr:S.toastOk) }} className="toast-in">
          <span style={S.toastDot} />
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ─── SUB-COMPOSANTS ───────────────────────────────────────────

function StatCard({ label, value, color, sub }) {
  return (
    <div style={S.statCard} className="stat-hover">
      <div style={S.statTop}><span style={S.statLabel}>{label}</span><div style={{ ...S.statDot,background:color }} /></div>
      <div style={{ ...S.statValue,color }}>{value}</div>
      <div style={S.statSub}>{sub}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const s=(status||"").toUpperCase();
  const map={
    ACCEPTED:  { color:"#16a34a",bg:"#dcfce7",label:"✓ Acceptée"   },
    REJECTED:  { color:"#dc2626",bg:"#fee2e2",label:"✗ Rejetée"    },
    PENDING:   { color:"#d97706",bg:"#fef3c7",label:"⏳ En attente" },
    SUBMITTED: { color:"#0ea5e9",bg:"#e0f2fe",label:"📤 Soumise"    },
    SIGNED:    { color:"#7c3aed",bg:"#f5f3ff",label:"✍ Signée"     },
    ARCHIVED:  { color:"#e11d48",bg:"#ffe4e6",label:"🗄️ Archivée"  },
    SYNCED:    { color:"#16a34a",bg:"#dcfce7",label:"✓ Synced"     },
    FAILED:    { color:"#dc2626",bg:"#fee2e2",label:"✗ Failed"     },
    C:         { color:"#2563eb",bg:"#dbeafe",label:"📝 Créée"     },
    V:         { color:"#16a34a",bg:"#dcfce7",label:"✅ Validée"    },
  };
  const cfg=map[s]||{color:"#94A3B8",bg:"#F8FAFC",label:status||"—"};
  return (
    <span style={{ display:"inline-flex",alignItems:"center",padding:"3px 9px",borderRadius:20,fontSize:"0.71rem",fontWeight:700,color:cfg.color,background:cfg.bg,whiteSpace:"nowrap" }}>
      {cfg.label}
    </span>
  );
}

function StateBox({ loading }) {
  return (
    <div style={{ background:"#fff",borderRadius:12,border:"1px solid #E8ECEF",padding:48,display:"flex",flexDirection:"column",alignItems:"center",gap:12 }}>
      {loading && <div style={{ width:32,height:32,border:"3px solid #F1F5F9",borderTop:"3px solid #6c63ff",borderRadius:"50%" }} className="spin" />}
      <p style={{ fontSize:"0.84rem",color:"#94A3B8",fontWeight:500 }}>{loading?"Chargement...":"Aucun résultat"}</p>
    </div>
  );
}

function EmptyState({ icon, text, action, actionLabel }) {
  return (
    <div style={{ background:"#fff",borderRadius:12,border:"1px solid #E8ECEF",padding:"40px",display:"flex",flexDirection:"column",alignItems:"center",gap:10 }}>
      <div style={{ fontSize:"2rem" }}>{icon}</div>
      <p style={{ fontSize:"0.84rem",color:"#94A3B8",fontWeight:500 }}>{text}</p>
      {action && <button style={S.btnPrimary} onClick={action}>{actionLabel}</button>}
    </div>
  );
}

// ─── STYLES (identiques à home.js) ───────────────────────────
const S = {
  root: { display:"flex",minHeight:"100vh",background:"#F8FAFC",fontFamily:"'Outfit','Segoe UI',sans-serif",color:"#0F172A" },
  sidebar:        { flexShrink:0,background:"#fff",borderRight:"1.5px solid #ededf5",display:"flex",flexDirection:"column",position:"fixed",top:0,left:0,bottom:0,zIndex:100,transition:"width 0.25s ease",overflow:"hidden",boxShadow:"4px 0 20px rgba(108,99,255,0.06)" },
  brand:          { display:"flex",alignItems:"center",gap:10,padding:"20px 16px 12px",cursor:"pointer",borderBottom:"1px solid #F1F5F9",flexShrink:0 },
  brandMark:      { width:34,height:34,background:"#6c63ff",borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1.1rem",flexShrink:0,color:"#fff" },
  brandName:      { fontSize:"1.05rem",fontWeight:800,color:"#6c63ff",whiteSpace:"nowrap" },
  collapseBtn:    { background:"#f0efff",border:"none",color:"#6c63ff",fontSize:"0.75rem",cursor:"pointer",padding:"4px 8px",borderRadius:6,margin:"6px 10px 4px",alignSelf:"flex-end",flexShrink:0 },
  sidebarNav:     { flex:1,overflowY:"auto",padding:"4px 8px" },
  sectionLabel:   { display:"flex",alignItems:"center",gap:6,fontSize:"0.63rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",padding:"10px 8px 3px",whiteSpace:"nowrap" },
  sectionDot:     { width:5,height:5,borderRadius:"50%",flexShrink:0 },
  sectionDivider: { height:1,background:"#ededf5",margin:"6px" },
  navItem:        { display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:8,cursor:"pointer",transition:"all 0.15s",marginBottom:1,fontSize:"0.84rem",fontWeight:500,whiteSpace:"nowrap",borderLeft:"3px solid transparent" },
  navIcon:        { fontSize:"0.95rem",width:20,textAlign:"center",flexShrink:0 },
  navLabel:       { overflow:"hidden",textOverflow:"ellipsis",flex:1 },
  sidebarFooter:  { borderTop:"1.5px solid #ededf5",padding:"10px",flexShrink:0 },
  userCard:       { display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius:10,background:"#F8FAFC",marginBottom:6 },
  userInitials:   { width:30,height:30,borderRadius:"50%",background:"#6c63ff",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"0.82rem",fontWeight:700,flexShrink:0 },
  userMeta:       { overflow:"hidden" },
  userNameText:   { fontSize:"0.8rem",fontWeight:600,color:"#1a1a2e",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" },
  logoutBtn:      { display:"flex",alignItems:"center",gap:6,width:"100%",padding:"7px 10px",borderRadius:8,border:"none",background:"#fff0f0",cursor:"pointer",fontSize:"0.78rem",fontWeight:600,color:"#dc2626",fontFamily:"inherit",transition:"all 0.15s" },
  main:          { flex:1,display:"flex",flexDirection:"column",minHeight:"100vh",transition:"margin-left 0.25s ease" },
  header:        { display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 32px",background:"#fff",borderBottom:"1.5px solid #ededf5",position:"sticky",top:0,zIndex:50,boxShadow:"0 2px 12px rgba(108,99,255,0.05)" },
  headerEyebrow: { fontSize:"0.68rem",fontWeight:600,letterSpacing:"0.08em",color:"#94A3B8",textTransform:"uppercase",marginBottom:3 },
  headerTitle:   { fontSize:"1.2rem",fontWeight:800,color:"#1a1a2e" },
  headerActions: { display:"flex",gap:8,alignItems:"center" },
  notifBtn:    { position:"relative",background:"#F8FAFC",border:"1.5px solid #E2E8F0",borderRadius:10,width:40,height:40,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",transition:"all 0.15s" },
  notifBadge:  { position:"absolute",top:-4,right:-4,background:"#dc2626",color:"#fff",fontSize:"0.55rem",fontWeight:800,minWidth:18,height:18,borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center",border:"2px solid #fff" },
  notifPanel:  { position:"absolute",top:"calc(100% + 10px)",right:0,width:340,background:"#fff",borderRadius:14,border:"1.5px solid #E2E8F0",boxShadow:"0 16px 48px rgba(0,0,0,0.12)",zIndex:200,overflow:"hidden" },
  notifHeader: { display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 16px",borderBottom:"1px solid #F1F5F9",background:"#F8FAFC" },
  notifAction: { background:"none",border:"none",cursor:"pointer",fontSize:"0.72rem",color:"#94A3B8",fontFamily:"inherit",fontWeight:600 },
  notifList:   { maxHeight:360,overflowY:"auto" },
  notifItem:   { padding:"12px 16px",borderBottom:"1px solid #F8FAFC",cursor:"pointer",transition:"background 0.1s" },
  notifEmpty:  { padding:"32px",display:"flex",flexDirection:"column",alignItems:"center",gap:4 },
  tabsBar:     { display:"flex",padding:"0 32px",background:"#fff",borderBottom:"1.5px solid #ededf5",overflowX:"auto" },
  tabBtn:      { display:"flex",alignItems:"center",gap:6,padding:"12px 14px",border:"none",borderBottom:"2px solid transparent",background:"none",cursor:"pointer",fontSize:"0.8rem",fontWeight:600,color:"#64748B",fontFamily:"inherit",transition:"all 0.15s",position:"relative",whiteSpace:"nowrap" },
  tabBtnActive:{ color:"#6c63ff",borderBottomColor:"#6c63ff" },
  tabCount:    { background:"#6c63ff",color:"#fff",fontSize:"0.6rem",fontWeight:800,padding:"1px 5px",borderRadius:20 },
  content:      { padding:"24px 32px",flex:1 },
  tabContent:   { display:"flex",flexDirection:"column",gap:16 },
  sectionTitle: { fontSize:"0.78rem",fontWeight:700,color:"#64748B",textTransform:"uppercase",letterSpacing:"0.07em",marginTop:4 },
  hero:             { display:"flex",alignItems:"center",justifyContent:"space-between",padding:"36px 32px",background:"linear-gradient(135deg,#fff 0%,#f0efff 60%,#e8f4fd 100%)",borderRadius:16,gap:32,overflow:"hidden" },
  heroContent:      { flex:1,maxWidth:480 },
  heroTag:          { display:"inline-block",background:"#f0efff",color:"#6c63ff",border:"1px solid #c7d2fe",borderRadius:20,padding:"3px 12px",fontSize:"0.72rem",fontWeight:600,textTransform:"uppercase",marginBottom:12 },
  heroTitle:        { fontSize:"2rem",fontWeight:800,color:"#1a1a2e",lineHeight:1.2,marginBottom:10 },
  heroDesc:         { fontSize:"0.9rem",color:"#64748b",lineHeight:1.7,marginBottom:20 },
  heroCta:          { background:"#6c63ff",color:"#fff",border:"none",padding:"11px 22px",borderRadius:10,fontSize:"0.88rem",fontWeight:700,cursor:"pointer",boxShadow:"0 8px 24px rgba(108,99,255,0.3)",fontFamily:"inherit" },
  heroCtaSecondary: { background:"transparent",color:"#6c63ff",border:"2px solid #6c63ff",padding:"11px 22px",borderRadius:10,fontSize:"0.88rem",fontWeight:700,cursor:"pointer",fontFamily:"inherit" },
  heroVisual:       { flex:1,position:"relative",height:180,display:"flex",alignItems:"center",justifyContent:"center" },
  floatingCard:     { position:"absolute",background:"#fff",borderRadius:12,padding:"10px 16px",fontWeight:600,fontSize:"0.82rem",boxShadow:"0 8px 24px rgba(108,99,255,0.15)",border:"1.5px solid rgba(108,99,255,0.1)",animation:"float 3s ease-in-out infinite" },
  statsGrid: { display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12 },
  statCard:  { background:"#fff",borderRadius:12,padding:"18px 20px",border:"1px solid #E8ECEF",transition:"all 0.2s",cursor:"pointer" },
  statTop:   { display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10 },
  statLabel: { fontSize:"0.7rem",fontWeight:600,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.06em" },
  statDot:   { width:7,height:7,borderRadius:"50%" },
  statValue: { fontSize:"2rem",fontWeight:800,letterSpacing:"-0.04em",lineHeight:1 },
  statSub:   { fontSize:"0.7rem",color:"#CBD5E1",marginTop:4,fontWeight:500 },
  quickGrid: { display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10 },
  quickCard: { background:"#fff",border:"1.5px solid #e8e8f0",borderRadius:12,padding:"16px",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:8,transition:"all 0.2s" },
  quickIcon: { width:40,height:40,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1.2rem" },
  quickLabel:{ fontSize:"0.78rem",fontWeight:600,color:"#334155",textAlign:"center" },
  tableCard:  { background:"#fff",borderRadius:12,border:"1px solid #E8ECEF",overflow:"hidden" },
  table:      { width:"100%",borderCollapse:"collapse" },
  th:         { padding:"11px 16px",textAlign:"left",fontSize:"0.67rem",fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.07em",borderBottom:"1px solid #F1F5F9",background:"#FAFAFA" },
  tr:         { borderBottom:"1px solid #F8FAFC",transition:"background 0.1s" },
  td:         { padding:"12px 16px",verticalAlign:"middle" },
  idNum:      { fontSize:"0.75rem",color:"#CBD5E1",fontWeight:700 },
  emailCell:  { fontSize:"0.8rem",color:"#64748B" },
  dateCell:   { fontSize:"0.77rem",color:"#94A3B8" },
  emptyState: { background:"#fff",borderRadius:12,border:"1px solid #E8ECEF",padding:"40px",display:"flex",flexDirection:"column",alignItems:"center",gap:10 },
  pill:       { padding:"4px 12px",borderRadius:20,fontSize:"0.75rem",fontWeight:600,whiteSpace:"nowrap" },
  btnSecondary: { display:"flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:9,border:"1px solid #E2E8F0",background:"#fff",cursor:"pointer",fontSize:"0.82rem",fontWeight:600,color:"#334155",fontFamily:"inherit",transition:"all 0.15s" },
  btnPrimary:   { padding:"9px 18px",borderRadius:9,border:"none",background:"#6c63ff",cursor:"pointer",fontSize:"0.82rem",fontWeight:700,color:"#fff",fontFamily:"inherit" },
  actionBtn:      { padding:"6px 12px",borderRadius:7,border:"1px solid #E2E8F0",background:"#fff",cursor:"pointer",fontSize:"0.75rem",fontWeight:600,color:"#334155",fontFamily:"inherit",transition:"all 0.15s" },
  actionBtnPdf:   { background:"#fff0f0",color:"#dc2626",border:"1px solid #fecaca" },
  actionBtnTrace: { background:"#f0f9ff",color:"#0891b2",border:"1px solid #bae6fd" },
  badge:         { display:"inline-flex",alignItems:"center",gap:4,padding:"3px 10px",borderRadius:20,fontSize:"0.72rem",fontWeight:700,whiteSpace:"nowrap" },
  factureCard:         { background:"#fff",borderRadius:14,border:"1.5px solid #e8e8f0",padding:"22px",display:"flex",flexDirection:"column",gap:14,transition:"all 0.2s" },
  factureHeader:       { display:"flex",justifyContent:"space-between",alignItems:"center" },
  factureMoneyRow:     { display:"flex",alignItems:"center",gap:0,background:"#F8FAFC",borderRadius:12,padding:"14px 18px" },
  factureMoneyItem:    { display:"flex",flexDirection:"column",gap:4,flex:1 },
  factureMoneyLabel:   { fontSize:"0.65rem",fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.05em" },
  factureMoneyVal:     { fontSize:"0.9rem",fontWeight:800,color:"#0F172A" },
  factureMoneyDivider: { width:1,height:36,background:"#E2E8F0",margin:"0 14px",flexShrink:0 },
  factureActions:      { display:"flex",gap:8,flexWrap:"wrap" },
  toolbar:          { display:"flex",gap:10,alignItems:"center",flexWrap:"wrap" },
  searchBox:        { flex:1,minWidth:220,display:"flex",alignItems:"center",gap:8,background:"#fff",border:"1.5px solid #e8e8f0",borderRadius:9,padding:"0 12px",height:40 },
  searchInput:      { flex:1,border:"none",outline:"none",fontSize:"0.84rem",background:"transparent",color:"#0F172A",fontFamily:"inherit" },
  searchClear:      { background:"none",border:"none",cursor:"pointer",color:"#94A3B8",fontSize:"1.1rem" },
  filterGroup:      { display:"flex",gap:6,flexWrap:"wrap" },
  filterChip:       { padding:"7px 12px",borderRadius:8,border:"1px solid #E2E8F0",background:"#fff",cursor:"pointer",fontSize:"0.75rem",fontWeight:600,color:"#64748B",fontFamily:"inherit",transition:"all 0.15s" },
  filterChipActive: { background:"#6c63ff",color:"#fff",borderColor:"#6c63ff" },
  advFiltersPanel:  { background:"#fff",borderRadius:12,border:"1.5px solid #c7d2fe",padding:"20px 24px",display:"flex",flexDirection:"column",gap:16 },
  exportMenu:     { position:"absolute",top:"calc(100% + 8px)",right:0,background:"#fff",borderRadius:12,border:"1.5px solid #E2E8F0",boxShadow:"0 12px 32px rgba(0,0,0,0.1)",zIndex:100,overflow:"hidden",minWidth:200 },
  exportMenuItem: { display:"flex",alignItems:"center",gap:12,padding:"12px 16px",width:"100%",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",textAlign:"left",transition:"background 0.1s",borderBottom:"1px solid #F1F5F9" },
  modalOverlay: { position:"fixed",inset:0,background:"rgba(15,23,42,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(4px)" },
  modal:        { background:"#fff",borderRadius:16,width:"90%",maxWidth:500,boxShadow:"0 24px 64px rgba(0,0,0,0.15)",overflow:"hidden",maxHeight:"90vh",overflowY:"auto" },
  modalHeader:  { display:"flex",alignItems:"center",justifyContent:"space-between",padding:"20px 24px",borderBottom:"1px solid #F1F5F9",position:"sticky",top:0,background:"#fff",zIndex:10 },
  modalClose:   { background:"#F8FAFC",border:"1px solid #E2E8F0",width:28,height:28,borderRadius:7,cursor:"pointer",fontSize:"1rem",display:"flex",alignItems:"center",justifyContent:"center",color:"#64748B" },
  modalBody:    { padding:"20px 24px" },
  modalFooter:  { display:"flex",gap:8,justifyContent:"flex-end",padding:"16px 24px",borderTop:"1px solid #F1F5F9",flexWrap:"wrap" },
  toast:   { position:"fixed",bottom:24,right:24,display:"flex",alignItems:"center",gap:10,padding:"12px 20px",borderRadius:10,fontSize:"0.84rem",fontWeight:600,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",zIndex:2000 },
  toastOk: { background:"#0F172A",color:"#fff" },
  toastErr:{ background:"#EF4444",color:"#fff" },
  toastDot:{ width:6,height:6,borderRadius:"50%",background:"rgba(255,255,255,0.5)",flexShrink:0 },
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Outfit', sans-serif; }
  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: #f1f1f1; }
  ::-webkit-scrollbar-thumb { background: #c7d2fe; border-radius: 3px; }
  .nav-hover:hover     { background: #f0efff !important; color: #6c63ff !important; }
  .btn-hover:hover     { background: #F8FAFC !important; border-color: #CBD5E1 !important; }
  .logout-hover:hover  { background: #fee2e2 !important; }
  .stat-hover:hover    { box-shadow: 0 4px 20px rgba(0,0,0,0.08); transform: translateY(-2px); }
  .facture-hover:hover { border-color: #6c63ff !important; box-shadow: 0 8px 24px rgba(108,99,255,0.1) !important; }
  .toast-in    { animation: toastIn 0.3s ease; }
  .notif-pulse { animation: notifPulse 2s ease-in-out infinite; }
  @keyframes notifPulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.15); } }
  @keyframes toastIn    { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
  @keyframes spin       { to { transform: rotate(360deg); } }
  .spin { animation: spin 0.8s linear infinite; }
  @keyframes float      { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-10px); } }
`;